import { Fragment, FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Typography, useMediaQuery } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { Booking } from '@spike/booking-model';
import { fetchOnboardingStepsThunk } from 'actions/onboardingSteps/OnboardingStepsActions';
import moment, { Moment } from 'moment-timezone';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import CreateBookingHeader from './CreateBookingHeader';
import FooterComponent from './FooterComponent';
import ClientSearch from './ClientSearch/ClientSearch';
import PetSearch from './PetSearch';
import { Option } from '@spike/model';
import DateSelector from './DateSelector';
import {
    RecurringSeriesCreated,
    NewBookingAlert,
    NewBookingAppointment,
    NewBookingOrder,
    NewBookingOrderItems,
    NewBookingOrderizable,
    PetService
} from '@spike/new-booking-model';
import { BookingLocationContext } from 'hooks/BookingLocationContext';
import { useApiClientWrapper, useAuth, useMarketplace, useMasterData, usePermissions, useTimeZone } from 'hooks';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import DepositSelector from './DepositSelector';
import Notes from './Notes';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import {
    fetchAlertsThunk,
    fetchPetServicesThunk,
    NewBookingsStatus,
    changeBookingStatusThunk,
    saveMultipleBookingForDepositThunk,
    saveMultipleBookingThunk
} from '@spike/new-bookings-action';
import { persistBookingCustomPrices } from './utils/bookingCustomPrices';
import { fetchEvaluationServiceThunk } from 'actions/services/ServicesActions';
import Service, { DaycareService } from 'model/Service';
import useNonInitialEffect from '@versiondos/hooks';
import { AppointmentsStatus, BookingsStatus, deleteBookingThunk } from '@spike/bookings-action';
import CollectDeposit from './CollectDeposit';
import AppointmentCreated from './UI/AppointmentCreated';
import ConfirmClosePopup from './UI/ConfirmClosePopup';
import clsx from 'clsx';
import Marketplace, { MarketplaceDepositType, OrganizationMarketplace } from '@spike/marketplace-model';
import { convertToNumber } from '@spike/price-utils';
import { WalkingMetadata, TrainingMetadata, status as AppointmentStatus } from '@spike/appointment-model';
import { clearPetsThunk, fetchPetsThunk, PetsState } from '@spike/pets-action';
import MultiplePets from './MultiplePets';
import AppointmentsPaginator from './MultiplePets/AppointmentsPaginator';
import { getAppointmentsTotalPages, paginateMultipleAppointments } from './MultiplePets/paginateMultipleAppointments';
import PetAndServices from './PetAndServices';
import type { MembershipsSelection } from './Memberships/UI/MembershipsSelector/MembershipsSelector';
import { ActionsPopup } from 'components/UI/ActionsPopup/ActionsPopup';
import { faPencil } from '@fortawesome/pro-light-svg-icons';
import { faRotate, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { Spinner } from 'components/UI';
import ComplementaryItem from './ExtraItems';
import {
    AppointmentActions,
    DataVaraible,
    AppointmentMultiple,
    itemTypes,
    PetServiceTotal,
    PetServiceWrapper,
    versionPetAndServices,
    wizardBooking,
    Discount,
    OrderizableTypes,
    ItemData,
    AddOnDate,
    Medication,
    Feeding,
    Daycare,
    Boarding,
    Transportation,
    Walking,
    Training
} from 'model/NewMultipleBooking';
import AddOnService from '@spike/addon-service-model';
import { Product } from '@spike/product-model';
import OrderDetail from 'components/OrderDetail';
import Checkout from 'components/Checkout2';
import { AppointmentOrderItem, Order } from '@spike/order-model';
import { ServicesStatus } from 'reducers/services/ServicesState';
import { EvaluationReport } from '@spike/report-model';
import { getFeedingSlotTimes } from 'utils/feedingSlotTimes';
import { isServiceDuration, resolveBookingType } from 'model/Service';
import { Room } from '@spike/room-model';
import { Task } from '@spike/task-model';
import { noop, set } from 'lodash';
import {
    createTaskDate,
    createMedicationTaskDate,
    getRangeDateByMomentTypeIdWithTimezone,
    shouldCreateTaskForDay
} from 'utils/dateTimeUtils';
import { DaySchedule } from './Daycare/FeedingsAndMedications/UI/MedicalSchedule/MedicalSchedule';
import DaycareFastCheckIn from './FastCheckIn/Daycare/DaycareFastCheckIn';
import { TransportationType } from './Transportation/UI/TransportationTypeSelector/TransportationTypeSelector';
import Analytics from 'analytics/client';
import { Events } from 'analytics/events';
import { getCapacityInfo } from './Daycare/Utils/GetCapacityInfo';
import { type Enrollment, type MembershipItem } from '@spike/membership-model';
import {
    isEnrollmentAvailableOnDate,
    resolveDiscountPayloadForSave,
    resolveOrderItemPriceForSave,
    shouldApplyMembershipRedeemOnSave
} from './utils/membershipDisplayPrice';
import {
    getBoardingStayDurationMinutes,
    getBoardingStayLineTotal,
    getBoardingStayNightsCount
} from './utils/boardingStayTotal';
import RecurringSeriesPanel from './RecurringSeries/RecurringSeriesPanel';
import RecurringSeriesViewPanel from './RecurringSeries/View/RecurringSeriesViewPanel';
import { STACKED_PANELS_QUERY } from './RecurringSeries/panelDocking';
import { DEFAULT_RECURRING_SERIES_CONFIG, RecurringSeriesConfig } from 'model/RecurringSeries';
import { RecurringSeriesTemplate, getSeriesAnchorDate, isRecurringSeriesConfigValid } from './utils/recurringSeries';
import { buildDaycareVariableDataPerSlot } from 'utils/daycareBulkSlotBuilder';
import { buildWalkingVariableDataPerSlot } from 'utils/walkingBulkSlotBuilder';
import { buildTrainingVariableDataPerSlot } from 'utils/trainingBulkSlotBuilder';
import { buildTransportationVariableDataPerSlot } from 'utils/transportationBulkSlotBuilder';
import { clearClientsThunk, getClientProfileOverviewThunk } from '@spike/clients-action';
import { showSuccess } from '@spike/notifications-action';
import type { TransportationLegType } from 'utils/transportationDateSlots';
import LocationSelector from './LocationSelector';

function enrollmentFromFlowPayload(
    flowPayload: unknown,
    serviceState: PetServiceWrapper | undefined
): Enrollment | undefined {
    const fromPayload = (flowPayload as { enrollment?: Enrollment })?.enrollment;
    if (fromPayload != null) return fromPayload;
    return serviceState?.enrollment;
}

function membershipItemFromFlowPayload(
    flowPayload: unknown,
    serviceState: PetServiceWrapper | undefined
): MembershipItem | undefined {
    const payload = flowPayload as {
        membershipItem?: MembershipItem;
        service?: { membershipItem?: MembershipItem };
    };
    if (payload.membershipItem != null) return payload.membershipItem;
    if (payload.service?.membershipItem != null) return payload.service.membershipItem;
    return serviceState?.membershipItem;
}

const DEFAULT_TAX_FOR_CUSTOM_ITEMS_SETTING_KEY = 'default_tax_for_custom_items_id';
const NO_TAX_SETTING_VALUE = '0';

const getDefaultCustomItemsTaxId = (marketplace: Marketplace): number | undefined => {
    const value = String(
        marketplace.settings?.find(setting => setting.key === DEFAULT_TAX_FOR_CUSTOM_ITEMS_SETTING_KEY)?.value ?? ''
    ).trim();

    if (!value || value === NO_TAX_SETTING_VALUE) {
        return undefined;
    }

    const taxId = Number(value);

    if (!Number.isFinite(taxId)) {
        return undefined;
    }

    const taxExists = marketplace.taxes.some(tax => Number(tax.id) === taxId);
    return taxExists ? taxId : undefined;
};

function shouldApplyEnrollmentForDate(enrollment: Enrollment | undefined, selectedDate: Moment | undefined): boolean {
    return isEnrollmentAvailableOnDate(enrollment, selectedDate);
}

const mergeDaycareDayWithClock = (day: Moment, clock: Moment, tz: string): Moment => {
    const d = day.clone().tz(tz).startOf('day');
    const c = clock.clone().tz(tz);
    return d.clone().hour(c.hour()).minute(c.minute()).second(0).millisecond(0);
};

const getDaycareDateSlotsForSave = (
    daycare: Daycare,
    fallbackDate: Moment,
    tz: string,
    isEditMode: boolean
): Array<{ date: Moment; dateEnd: Moment }> => {
    if (isEditMode) {
        const from = (daycare.from || fallbackDate).clone();
        const to = daycare.to?.clone() || from.clone().add(daycare.service?.durationService || 0, 'minutes');
        return [{ date: from, dateEnd: to }];
    }

    const selected = daycare.selectedDates ?? [];
    if (selected.length === 0) {
        const from = daycare.from || fallbackDate;
        const to = daycare.to || from.clone().add(daycare.service?.durationService || 0, 'minutes');
        return [{ date: from.clone(), dateEnd: to.clone() }];
    }

    if (!daycare.from || !daycare.to) {
        const from = daycare.from || fallbackDate;
        const to = from.clone().add(daycare.service?.durationService || 0, 'minutes');
        return [{ date: from.clone(), dateEnd: to.clone() }];
    }

    const uniqByDay = new Map<string, Moment>();
    selected.forEach(d => {
        const dayStart = d.clone().tz(tz).startOf('day');
        uniqByDay.set(dayStart.format('YYYY-MM-DD'), dayStart);
    });
    const sortedDays = Array.from(uniqByDay.values()).sort((a, b) => a.valueOf() - b.valueOf());

    return sortedDays.map(dayStart => ({
        date: mergeDaycareDayWithClock(dayStart, daycare.from!, tz),
        dateEnd: mergeDaycareDayWithClock(dayStart, daycare.to!, tz)
    }));
};

const findDaycareVariableDataIndexForEdit = (
    variableData: Array<DataVaraible>,
    serviceId: number | undefined,
    editDate?: Moment
): number =>
    variableData.findIndex(
        entry =>
            entry.itemData?.flow === 'daycare' &&
            entry.service?.service?.id === serviceId &&
            (editDate == null || Boolean(entry.date?.isSame(editDate)))
    );

const findWalkingVariableDataIndexForEdit = (
    variableData: Array<DataVaraible>,
    serviceId: number | undefined,
    editDate?: Moment
): number =>
    variableData.findIndex(
        entry =>
            entry.itemData?.flow === 'walking' &&
            entry.service?.service?.id === serviceId &&
            (editDate == null || Boolean(entry.date?.isSame(editDate)))
    );

const findTrainingVariableDataIndexForEdit = (
    variableData: Array<DataVaraible>,
    serviceId: number | undefined,
    editDate?: Moment
): number =>
    variableData.findIndex(
        entry =>
            entry.itemData?.flow === 'training' &&
            entry.service?.service?.id === serviceId &&
            (editDate == null || Boolean(entry.date?.isSame(editDate)))
    );

const findTransportationVariableDataIndexForEdit = (
    variableData: Array<DataVaraible>,
    serviceId: number | undefined,
    editDate?: Moment,
    legType?: TransportationLegType
): number =>
    variableData.findIndex(
        entry =>
            entry.itemData?.flow === 'transportation' &&
            entry.service?.service?.id === serviceId &&
            (legType == null || entry.itemData?.type === legType) &&
            (editDate == null || Boolean(entry.date?.isSame(editDate)))
    );

interface CreateBookingProps {
    fromClient?: boolean;
    staffId?: number;
    at?: Moment;
    parentID?: number;
    petID?: number;
    parentName?: string;
    appointmentId?: number;
    order?: Order;
    appointmentItem?: AppointmentOrderItem;
    detailView?: boolean;
    clientAdded?: boolean;
    petAdded?: boolean;
    petsWithoutSelect?: boolean;
    multipleAppointments?: Array<AppointmentMultiple>;
    isEvaluation?: boolean;
    isDaycareFastCheckIn?: boolean;
    flow?: 'grooming' | 'dental' | 'daycare' | 'boarding' | 'transportation' | 'walking' | 'training';
    onAddClient?: () => void;
    onAddPet?: (clientId: number) => void;
    onSave?: () => void;
    onClose: (value?: boolean) => void;
    onBooked: () => void;
    onStartBooking?: () => void;
    onChangeTime?: (bookedAt?: Moment, durationInMinutes?: number, staffId?: number) => void;
    onRemoveClient?: () => void;
    onShowBlockedTime?: () => void;
    onChangeNewBooking?: (value: boolean) => void;
    onShowPetCard?: (petId: number, clientId: number) => void;
    onAddEvaluation?: (petId: number, clientId: number) => void;
    onViewResults?: (evaluationReport?: EvaluationReport) => void;
    onRefreshBooking?: () => void;
    initialMembershipSelection?: MembershipsSelection;
    initialMembershipServiceId?: number;
    /** Set when the booking converts a waitlist request (sent as `waitlist_id`). */
    waitlistId?: number;
    /** Location where the new booking will be created. Defaults to the logged-in location. */
    marketplaceId?: number;
}

const marketplaceForBookingLocation = (
    currentMarketplace: Marketplace,
    selectedMarketplaceId: number | undefined,
    selectedOrganizationMarketplace?: OrganizationMarketplace
): Marketplace => {
    if (!selectedMarketplaceId || selectedMarketplaceId === currentMarketplace.id || !selectedOrganizationMarketplace) {
        return currentMarketplace;
    }

    return {
        ...currentMarketplace,
        id: selectedOrganizationMarketplace.id,
        basics: {
            ...currentMarketplace.basics,
            businessName: selectedOrganizationMarketplace.name,
            address: {
                ...currentMarketplace.basics.address,
                id: selectedOrganizationMarketplace.addressId
            }
        },
        schedule: {
            ...currentMarketplace.schedule,
            timeZone: selectedOrganizationMarketplace.timeZone
        }
    };
};

export const getRoomUnitId = (
    room?: Room,
    flow?: 'grooming' | 'dental' | 'daycare' | 'boarding' | 'transportation' | 'walking' | 'training',
    roomUnitId?: number
): number | undefined => {
    if (!room?.units?.length || (flow === 'boarding' && !roomUnitId)) return undefined;

    const activeUnits = room.units.filter(unit => unit.active !== false);

    if (activeUnits.length === 0) return undefined;

    const hasBySizeRules = activeUnits.some(
        unit => unit.sizeEligibility !== null && unit.sizeEligibility !== undefined
    );

    if (hasBySizeRules) {
        const availableBySizeUnit = activeUnits.find(unit => unit.available);
        return availableBySizeUnit?.id ?? activeUnits[0]?.id;
    }

    const { max } = getCapacityInfo(room);

    if (!max) return activeUnits[0]?.id;

    const availableUnit = activeUnits.find(unit => (unit.occupancy ?? 0) < max);

    return availableUnit?.id ?? activeUnits[0]?.id;
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%'
        },
        containerStacked: {
            flexShrink: 0
        },
        createRecurringLink: {
            gap: 6,
            display: 'flex',
            cursor: 'pointer',
            alignItems: 'center',
            color: '#000000',
            whiteSpace: 'nowrap',
            borderBottom: '1.5px solid #000000'
        },
        createRecurringLinkDisabled: {
            cursor: 'default',
            color: '#BDBDBD',
            pointerEvents: 'none',
            borderBottomColor: '#BDBDBD'
        },
        createRecurringIcon: {
            width: 16,
            height: 16,
            fontSize: 15.6
        },
        createRecurringLabel: {
            fontSize: 16,
            fontWeight: 500,
            lineHeight: '27px'
        },
        headerContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            margin: '17px 27px 27px 27px',
            [theme.breakpoints.down('sm')]: {
                margin: '20px 30px 30px 30px'
            },
            [theme.breakpoints.up('md')]: {
                margin: '20px 30px 27px 30px'
            }
        },
        appointmentContainer: {
            margin: '0px 26px 0px 26px',
            [theme.breakpoints.up('md')]: {
                margin: '0px 30px 0px 30px'
            }
        },
        totalContainer: {
            height: '15%',
            borderTop: '1px solid #D4D4D4'
        },
        marginTotalContainer: {
            marginTop: 11,
            marginBottom: 22
        },
        sectionContainer: {
            paddingTop: 18,
            [theme.breakpoints.up('md')]: {
                paddingTop: 20
            }
        },
        locationSection: {
            paddingBottom: 18,
            [theme.breakpoints.up('md')]: {
                paddingBottom: 20
            }
        },
        appointmentCreatedContainer: {
            height: '87%'
        },
        withoutEvents: {
            pointerEvents: 'none'
        },
        marginTop: {
            marginTop: -20
        },
        loadingContainer: {
            width: '100%',
            display: 'flex',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center'
        }
    })
);

export const CreateBooking: FunctionComponent<CreateBookingProps> = props => {
    const classes = useStyles();
    const defaultTimezone = useTimeZone();
    const baseApiClientWrapper = useApiClientWrapper();
    const auth = useAuth();
    const marketplace = useMarketplace();
    const { organizationMarketplaces } = useOrganizationMarketplaces();

    const bookingLocations = useMemo(() => {
        const organizationLocations = organizationMarketplaces
            .filter(location => location.active)
            .map(location => ({
                id: location.id,
                name: location.name,
                timeZone: location.timeZone
            }));

        return organizationLocations.length > 0 || marketplace.id === undefined
            ? organizationLocations
            : [
                  {
                      id: marketplace.id,
                      name: marketplace.basics.businessName,
                      timeZone: marketplace.schedule.timeZone
                  }
              ];
    }, [organizationMarketplaces, marketplace]);

    const isEditingBooking = Boolean(props.order || props.appointmentItem || props.detailView);
    const showLocationSelector = bookingLocations.length > 1 && !isEditingBooking && !props.isDaycareFastCheckIn;
    const locationReadOnly = Boolean(props.waitlistId || props.fromClient);

    const [selectedMarketplaceId, setSelectedMarketplaceId] = useState<number | undefined>(
        () =>
            props.marketplaceId ??
            (showLocationSelector && !props.fromClient && !props.waitlistId
                ? undefined
                : marketplace.id ?? auth.marketplaceId)
    );

    const selectedLocation = bookingLocations.find(location => location.id === selectedMarketplaceId);
    const timezone = selectedLocation?.timeZone ?? defaultTimezone;
    const marketplaceId = selectedMarketplaceId ?? marketplace.id ?? auth.marketplaceId;
    const selectedOrganizationMarketplace = organizationMarketplaces.find(location => location.id === marketplaceId);
    const apiClientWrapper = {
        ...baseApiClientWrapper,
        marketplaceId: marketplaceId!,
        timeZone: timezone,
        marketplace: marketplaceForBookingLocation(marketplace, marketplaceId, selectedOrganizationMarketplace)
    };
    const masterData = useMasterData();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isLargeDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const isStackedRecurring = useMediaQuery(STACKED_PANELS_QUERY);
    const permissions = usePermissions();
    const takeDepositPayments = permissions.checkout.takePayments ?? false;

    const alerts = useSelector<RootState, Array<NewBookingAlert> | undefined>(state => state.newBookings.alerts);
    const newBookingsStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);
    const savedBooking = useSelector<RootState, Booking | undefined>(state => state.newBookings.bookingSavedForDeposit);
    const appointmentsStatus = useSelector<RootState, AppointmentsStatus>(state => state.appointments.status);
    const petsState = useSelector<RootState, PetsState>(state => state.pets);
    const bookingStatus = useSelector<RootState, BookingsStatus>(state => state.bookings.status);
    const petServices = useSelector<RootState, Array<PetService>>(state => state.newBookings.petServices);
    const serviceStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);
    const bookingDone = useSelector<RootState, boolean>(
        state => state.onboardingSteps.onboardingSteps.bookingDone ?? false
    );
    const evaluationService = useSelector<RootState, Service | null | undefined>(
        state => state.services.evaluationService
    );
    const daycareServices = useSelector<RootState, Array<DaycareService>>(
        state => state.services.daycareServices || []
    );
    const [parentId, setParentId] = useState<number | undefined>(props.parentID);
    const [parentName, setParentName] = useState<string | undefined>(props.parentName);
    const [petId, setPetId] = useState<number | undefined>(props.petID);
    const [recurringConfig, setRecurringConfig] = useState<RecurringSeriesConfig | undefined>(undefined);
    const [showRecurringPanel, setShowRecurringPanel] = useState(false);
    const [showRecurringSeriesPanel, setShowRecurringSeriesPanel] = useState(false);
    const [recurringSeriesCreated, setRecurringSeriesCreated] = useState<RecurringSeriesCreated | undefined>(undefined);
    const [service, setService] = useState<PetServiceWrapper | undefined>();
    const [date, setDate] = useState<Moment>(props.at ? props.at : moment().tz(timezone));
    const [dateEnd, setDateEnd] = useState<Moment | undefined>(undefined);
    const [dateToEdit, setDateToEdit] = useState<Moment | undefined>(undefined);
    const [staffId, setStaffId] = useState<number | undefined>();
    const [showDepositAndNote, setShowDepositAndNote] = useState(false);
    const [isCollectDeposit, setIsCollectDeposit] = useState(false);
    const [notes, setNotes] = useState(props.multipleAppointments?.find(a => a.notes)?.notes ?? '');
    const [saving, setSaving] = useState(false);
    const [depositCollect, setDepositCollect] = useState(false);
    const [appointmentCreated, setAppointmentCreated] = useState(false);
    const [depositSkipped, setDepositSkipped] = useState(false);
    const [closing, setClosing] = useState({ close: false, tabChange: false });
    const [loading, setLoading] = useState<boolean>(false);
    const [touched, setTouched] = useState(false);
    const [showPetAndServices, setShowPetAndServices] = useState(false);
    const [showComplementaryItem, setShowComplementaryItem] = useState(false);
    const [versionPetAndService, setVersionPetAndService] = useState<versionPetAndServices | undefined>(undefined);
    const [multipleAppointments, setMultipleAppointments] = useState<Array<AppointmentMultiple>>(
        props.multipleAppointments || []
    );
    const [appointmentsPage, setAppointmentsPage] = useState(1);
    const closedForMultiDaycareRef = useRef(false);
    const multiDaycareCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [serviceTotal, setServiceTotal] = useState<PetServiceTotal | undefined>();
    const [flow, setFlow] = useState<
        | 'grooming'
        | 'dental'
        | 'daycare'
        | 'boarding'
        | 'transportation'
        | 'walking'
        | 'training'
        | 'memberships'
        | undefined
    >(props.flow);
    const [exceededDeposit, setExceededDeposit] = useState<boolean>(
        marketplace.deposit?.type === MarketplaceDepositType.Amount &&
            convertToNumber(marketplace.deposit?.value || '0') > (serviceTotal?.price || service?.price || 0)
    );
    const [showAlertBusinessHour, setShowAlertBusinessHour] = useState<boolean>(false);
    const [showActions, setShowActions] = useState<AppointmentActions>({
        date: undefined,
        staffId: undefined,
        service: undefined,
        petId: undefined,
        product: undefined,
        addOn: undefined,
        custom: undefined,
        type: undefined,
        discount: undefined,
        itemData: undefined,
        dateEnd: undefined
    });
    const [loadingAppointments, setLoadingAppointments] = useState<boolean>(false);
    const [rebook, setRebook] = useState<boolean>(false);
    const [type, setType] = useState<itemTypes | undefined>(undefined);
    const [discount, setDiscount] = useState<Discount>({
        type: 'amount',
        value: 0
    });
    const [product, setProduct] = useState<Product | undefined>(undefined);
    const [addOn, setAddOn] = useState<AddOnService | undefined>(undefined);
    const [custom, setCustom] = useState<string | undefined>(undefined);
    const [taxId, setTaxId] = useState<number | undefined>(undefined);
    const [servicesUsed, setServicesUsed] = useState<Array<number>>([]);
    const [petsUsed, setPetsUsed] = useState<Array<number>>([]);
    const [quantity, setQuantity] = useState<number>(1);
    const [amount, setAmount] = useState<number>(0);
    const [orderData, setOrderData] = useState<{
        orderId?: number;
        bookingId?: number;
        showDetail: boolean;
        showCheckout: boolean;
    }>({
        showDetail: false,
        showCheckout: false
    });
    const [datesAddOn, setDatesAddOn] = useState<Array<AddOnDate> | undefined>(undefined);
    const [itemsUsed, setItemsUsed] = useState<Array<number>>([]);
    const [transportationType, setTransportationType] = useState<TransportationType | undefined>(undefined);
    const [membershipPrefillEnabled, setMembershipPrefillEnabled] = useState<boolean>(
        Boolean(props.initialMembershipSelection && props.initialMembershipServiceId != null && !props.isEvaluation)
    );

    const [isDaycareBoardingTransportationTrainingOrWalking, setIsDaycareBoardingTransportationTrainingOrWalking] =
        useState<boolean>(false);

    const [depositPaidAmount, setDepositPaidAmount] = useState<number>(0);

    const isRecurring = recurringConfig !== undefined;

    useEffect(() => {
        if (!parentId) return;
        dispatch(getClientProfileOverviewThunk(apiClientWrapper, parentId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parentId]);

    useEffect(() => {
        return () => {
            if (multiDaycareCloseTimerRef.current) {
                clearTimeout(multiDaycareCloseTimerRef.current);
            }
        };
    }, []);

    const [isEvaluationActive, setIsEvaluationActive] = useState<boolean>(props.isEvaluation || false);

    useEffect(() => {
        if (!appointmentCreated) return;
        if (bookingDone) return;
        if (!marketplaceId) return;

        try {
            const key = `event_one_shot_${Events.APPOINTMENT_BOOKED}_${marketplaceId}`;
            if (localStorage.getItem(key) === '1') return;

            Analytics.track(Events.APPOINTMENT_BOOKED as any, {
                marketplace_id: marketplaceId
            });
            localStorage.setItem(key, '1');
        } catch (e) {
            // non-blocking
        }
    }, [appointmentCreated, bookingDone, marketplaceId]);

    const { morning, afternoon, evening } = getFeedingSlotTimes(marketplace, masterData);

    const isBoarding = useMemo(
        () =>
            multipleAppointments.some((appt: AppointmentMultiple) =>
                appt.variableData.some((data: DataVaraible) => data.itemData?.flow === 'boarding')
            ) ?? false,
        [multipleAppointments]
    );

    const appointmentsTotalPages = useMemo(
        () => getAppointmentsTotalPages(multipleAppointments),
        [multipleAppointments]
    );

    const paginatedMultipleAppointments = useMemo(
        () => paginateMultipleAppointments(multipleAppointments, appointmentsPage),
        [multipleAppointments, appointmentsPage]
    );

    // Session occupancy only counts saved appointments, so the walking and training drawers need
    // the bookings already added here. The one being edited is left out or it would count against itself.
    const draftSessionBookings = useMemo(() => {
        const isEdit = versionPetAndService === versionPetAndServices.edit;
        const isSessionFlow = (data: DataVaraible) =>
            data.itemData?.flow === 'walking' || data.itemData?.flow === 'training';

        return multipleAppointments.flatMap((appt: AppointmentMultiple) => {
            const editedIndex =
                isEdit && appt.petId === petId
                    ? appt.variableData.findIndex(
                          entry =>
                              isSessionFlow(entry) &&
                              entry.service?.service?.id === service?.service?.id &&
                              (dateToEdit == null || Boolean(entry.date?.isSame(dateToEdit)))
                      )
                    : -1;

            return appt.variableData.filter(
                (data: DataVaraible, index: number) => isSessionFlow(data) && index !== editedIndex
            );
        });
    }, [multipleAppointments, versionPetAndService, petId, service, dateToEdit]);

    useEffect(() => {
        setAppointmentsPage(prev => Math.min(prev, appointmentsTotalPages));
    }, [appointmentsTotalPages]);

    const handleAddEvaluation = (petId: number, clientId: number) => {
        setIsEvaluationActive(true);
        setShowPetAndServices(true);
        setPetId(petId);
    };

    const handlerRemove = (value: string) => {
        switch (value) {
            case wizardBooking.PetParent:
                setParentId(undefined);
                setParentName('');
                setParentId(undefined);
                setPetId(undefined);
                setShowDepositAndNote(false);
                setMultipleAppointments([]);
                break;
            case wizardBooking.Service:
                setService(undefined);
                setDiscount({
                    type: 'amount',
                    value: 0
                });
                break;
            case wizardBooking.Staff:
                setStaffId(undefined);
                break;
            case wizardBooking.Deposit:
                setIsCollectDeposit(false);
                break;
            case wizardBooking.Notes:
                setNotes('');
                break;
        }
    };

    const handleRemove = () => {
        handlerRemove(wizardBooking.PetParent);
        handlerRemove(wizardBooking.Service);
        handlerRemove(wizardBooking.Staff);
        handlerRemove(wizardBooking.Deposit);
        handlerRemove(wizardBooking.Notes);
        setOrderData(prev => ({ ...prev, showDetail: false, showCheckout: false }));
    };

    const changeLocationHandler = (nextMarketplaceId: number) => {
        if (nextMarketplaceId === selectedMarketplaceId) {
            return;
        }

        const nextTimezone =
            bookingLocations.find(location => location.id === nextMarketplaceId)?.timeZone ?? defaultTimezone;

        setSelectedMarketplaceId(nextMarketplaceId);
        setDate(current => current.clone().tz(nextTimezone));
        handleRemove();
        setRecurringConfig(undefined);
        setShowRecurringPanel(false);
        dispatch(clearPetsThunk());
        dispatch(clearClientsThunk());
    };

    const handleCloseBooking = (value?: boolean) => {
        if (recurringSeriesCreated) {
            handleBookedBooking();
            return;
        }
        if (!value && !appointmentCreated) {
            props.onClose(value);
        } else {
            handleCloseWithOutModal();
        }
    };

    const handleCloseWithOutModal = () => {
        props.onClose(true);
    };

    const handleBookedBooking = () => {
        handleRemove();
        props.onBooked();
    };

    const handleBooked = async () => {
        setSaving(true);

        if (appointmentCreated) {
            handleBookedBooking();
            return;
        }

        try {
            await persistBookingCustomPrices(dispatch, apiClientWrapper, multipleAppointments);
        } catch {
            // Custom price persistence is best-effort; booking save continues.
        }

        if (isCollectDeposit && !exceededDeposit) {
            const multipleBookingFormatted: Array<NewBookingAppointment> = getMultipleBookingToSave();
            const complementaryItemsFormatted: NewBookingOrder = getComplementaryItemsToSave();

            dispatch(
                saveMultipleBookingForDepositThunk(
                    apiClientWrapper,
                    multipleBookingFormatted,
                    complementaryItemsFormatted,
                    props.waitlistId
                )
            );
            return;
        }

        const multipleBookingFormatted: Array<NewBookingAppointment> = getMultipleBookingToSave();
        const complementaryItemsFormatted: NewBookingOrder = getComplementaryItemsToSave();
        const isMultiBulk = isMultiServiceBulkBooking();

        dispatch(
            saveMultipleBookingThunk(
                apiClientWrapper,
                multipleBookingFormatted,
                complementaryItemsFormatted,
                undefined,
                undefined,
                undefined,
                props.waitlistId
            )
        );

        if (isMultiBulk) {
            closeDrawerAfterMultiDaycareBook();
        }
    };

    const haveMultiplePets = () => {
        return (
            multipleAppointments.length > 1 ||
            (multipleAppointments.length === 1 && multipleAppointments[0].variableData.length > 1)
        );
    };

    const isMultiServiceBulkBooking = () => {
        const serviceEntries = multipleAppointments.flatMap(appt => appt.variableData?.filter(d => d.service) ?? []);
        const daycareCount = serviceEntries.filter(
            d => d.itemData?.flow === 'daycare' || d.service?.businessArea === 'daycare'
        ).length;
        const walkingCount = serviceEntries.filter(
            d => d.itemData?.flow === 'walking' || d.service?.businessArea === 'walking'
        ).length;
        const trainingCount = serviceEntries.filter(
            d => d.itemData?.flow === 'training' || d.service?.businessArea === 'training'
        ).length;
        const transportationDays = new Set(
            serviceEntries
                .filter(d => d.itemData?.flow === 'transportation' || d.service?.businessArea === 'transportation')
                .map(d => d.date?.clone().startOf('day').format('YYYY-MM-DD'))
                .filter(Boolean)
        );
        return daycareCount >= 2 || walkingCount >= 2 || trainingCount >= 2 || transportationDays.size >= 2;
    };

    const closeDrawerAfterMultiDaycareBook = () => {
        closedForMultiDaycareRef.current = true;
        dispatch(showSuccess('Appointments are being created'));

        if (multiDaycareCloseTimerRef.current) {
            clearTimeout(multiDaycareCloseTimerRef.current);
        }
        multiDaycareCloseTimerRef.current = setTimeout(() => {
            multiDaycareCloseTimerRef.current = null;
            if (!bookingDone && marketplaceId) {
                try {
                    const key = `event_one_shot_${Events.APPOINTMENT_BOOKED}_${marketplaceId}`;
                    if (localStorage.getItem(key) !== '1') {
                        Analytics.track(Events.APPOINTMENT_BOOKED as any, {
                            marketplace_id: marketplaceId
                        });
                        localStorage.setItem(key, '1');
                    }
                } catch (e) {
                    // non-blocking
                }
            }
            props.onChangeNewBooking && props.onChangeNewBooking(false);
            if (!bookingDone) {
                dispatch(fetchOnboardingStepsThunk());
            }
            handleBookedBooking();
        }, 500);
    };

    const buildAppointmentToSave = (
        dateAppt?: Moment,
        petIdAppt?: number,
        serviceAppt?: PetServiceWrapper,
        staffIdAppt?: number,
        discountAppt?: Discount,
        flowAppt?:
            | 'grooming'
            | 'dental'
            | 'daycare'
            | 'boarding'
            | 'transportation'
            | 'walking'
            | 'training'
            | 'evaluation'
            | 'memberships'
    ) => {
        setShowPetAndServices(false);
        setShowComplementaryItem(false);
        setIsEvaluationActive(props.isEvaluation || false);
        setVersionPetAndService(undefined);
        backPetAndServicesOrComplementaryItem();
        setType(itemTypes.service);
        const petFounded = multipleAppointments.findIndex((app: AppointmentMultiple) => app.petId === petId);
        if (petFounded !== -1) {
            const newMultipleAppointments = [...multipleAppointments];
            if (versionPetAndService === versionPetAndServices.edit) {
                const serviceFounded = multipleAppointments[petFounded]?.variableData?.findIndex(
                    (app: DataVaraible) => app.service?.service?.id === service?.service?.id
                );
                if (serviceFounded !== -1) {
                    newMultipleAppointments[petFounded].variableData[serviceFounded] = {
                        service: serviceAppt!,
                        staffId: staffIdAppt!,
                        date: dateAppt || date,
                        itemData: {
                            discount: {
                                type: discountAppt?.type || 'amount',
                                value: discountAppt?.value ?? 0
                            },
                            flow: flowAppt as ItemData['flow']
                        }
                    };
                    setMultipleAppointments(newMultipleAppointments);
                }
            } else {
                newMultipleAppointments[petFounded] = {
                    ...newMultipleAppointments[petFounded],
                    variableData: [
                        ...newMultipleAppointments[petFounded].variableData,
                        {
                            service: serviceAppt!,
                            staffId: staffIdAppt!,
                            date: dateAppt || date,
                            itemData: {
                                discount: {
                                    type: discountAppt?.type || 'amount',
                                    value: discountAppt?.value ?? 0
                                },
                                flow: flowAppt as ItemData['flow']
                            }
                        }
                    ]
                };
                setMultipleAppointments(newMultipleAppointments);
            }
        } else {
            const appointmentToSave: AppointmentMultiple = {
                clientId: parentId!,
                petId: petIdAppt!,
                createdByStaffId: auth.user!.staffId,
                variableData: [
                    {
                        service: serviceAppt!,
                        staffId: staffIdAppt!,
                        date: dateAppt || date,
                        itemData: {
                            discount: {
                                type: discountAppt?.type || 'amount',
                                value: discountAppt?.value ?? 0
                            },
                            flow: flowAppt as ItemData['flow']
                        }
                    }
                ],
                notes: notes
            };
            setMultipleAppointments(prev => [...prev, appointmentToSave]);
        }
    };

    const handlerSavePetAndServices = (
        dateAppt?: Moment,
        petIdAppt?: number,
        serviceAppt?: PetServiceWrapper,
        staffIdAppt?: number,
        discountAppt?: Discount,
        flowAppt?:
            | 'grooming'
            | 'dental'
            | 'daycare'
            | 'boarding'
            | 'transportation'
            | 'walking'
            | 'training'
            | 'evaluation'
            | 'memberships'
    ) => {
        let nextService = serviceAppt;
        const effectiveDate = dateAppt ?? date;
        if (nextService?.enrollment && !shouldApplyEnrollmentForDate(nextService.enrollment, effectiveDate)) {
            nextService = { ...nextService, enrollment: undefined };
        }
        buildAppointmentToSave(dateAppt, petIdAppt, nextService, staffIdAppt, discountAppt, flowAppt);
    };

    const handlerSaveDaycare = (
        daycare: Daycare,
        feedings: Array<Feeding>,
        medications: Array<Medication>,
        petIdSelected: number
    ) => {
        setShowPetAndServices(false);
        setShowComplementaryItem(false);
        setVersionPetAndService(undefined);
        backPetAndServicesOrComplementaryItem();
        setType(itemTypes.service);
        const isDaycareEdit = versionPetAndService === versionPetAndServices.edit;
        const daycareWithMembership: Daycare = {
            ...daycare,
            enrollment: enrollmentFromFlowPayload(daycare, service),
            membershipItem: membershipItemFromFlowPayload(daycare, service)
        };
        const petFounded = multipleAppointments.findIndex((app: AppointmentMultiple) => app.petId === petId);
        const membershipUsesAlreadyAllocated =
            !isDaycareEdit && petFounded !== -1 && daycareWithMembership.enrollment?.id != null
                ? (multipleAppointments[petFounded]?.variableData ?? []).filter(
                      entry =>
                          entry.itemData?.flow === 'daycare' &&
                          entry.service?.enrollment?.id === daycareWithMembership.enrollment?.id &&
                          shouldApplyMembershipRedeemOnSave(entry.service)
                  ).length
                : 0;
        const serviceId =
            daycareWithMembership.service?.serviceId ?? service?.service?.id ?? daycare.service?.serviceId;
        const catalogFromList = daycareServices.find(s => s.serviceId === serviceId)?.price;
        const catalogFromPetServices = petServices.find(ps => ps.service.id === serviceId)?.price;
        const catalogOverride =
            service?.beforePrice ??
            daycareWithMembership.service?.beforePrice ??
            catalogFromList ??
            catalogFromPetServices;
        const newDaycareVariableData = buildDaycareVariableDataPerSlot(
            daycareWithMembership,
            feedings,
            medications,
            date,
            timezone,
            isDaycareEdit,
            membershipUsesAlreadyAllocated,
            catalogOverride
        );

        if (petFounded !== -1) {
            const newMultipleAppointments = [...multipleAppointments];
            if (isDaycareEdit) {
                const editDate = dateToEdit ?? daycare.from;
                const serviceFounded = findDaycareVariableDataIndexForEdit(
                    multipleAppointments[petFounded]?.variableData ?? [],
                    service?.service?.id,
                    editDate
                );
                if (serviceFounded !== -1) {
                    newMultipleAppointments[petFounded].variableData[serviceFounded] = newDaycareVariableData[0];
                    setMultipleAppointments(newMultipleAppointments);
                }
            } else {
                newMultipleAppointments[petFounded] = {
                    ...newMultipleAppointments[petFounded],
                    variableData: [...newMultipleAppointments[petFounded].variableData, ...newDaycareVariableData]
                };
                setMultipleAppointments(newMultipleAppointments);
            }
        } else {
            const appointmentToSave: AppointmentMultiple = {
                clientId: parentId!,
                petId: petIdSelected!,
                createdByStaffId: auth.user!.staffId,
                variableData: newDaycareVariableData,
                notes: notes
            };
            setMultipleAppointments(prev => [...prev, appointmentToSave]);
        }
    };

    const handlerSaveBoarding = (
        boarding: Boarding,
        feedings: Array<Feeding>,
        medications: Array<Medication>,
        petIdSelected: number
    ) => {
        setShowPetAndServices(false);
        setShowComplementaryItem(false);
        setVersionPetAndService(undefined);
        backPetAndServicesOrComplementaryItem();
        setType(itemTypes.service);

        const selectedOffer = boarding.service?.selectedOffer ?? service?.selectedOffer;
        const nightlyPrice = selectedOffer != null ? selectedOffer.pricePerNight : boarding.price;
        const boardingStayTotal = getBoardingStayLineTotal({
            date: boarding.from,
            dateEnd: boarding.to,
            servicePrice: nightlyPrice,
            selectedOffer: selectedOffer ?? null
        });

        const boardingItemData: ItemData = {
            discount: {
                type: 'amount',
                value: 0
            },
            flow: 'boarding',
            ...(selectedOffer != null ? { price: nightlyPrice, total: boardingStayTotal } : {})
        };

        const boardingToService: PetServiceWrapper = {
            service: {
                id: boarding.service?.serviceId || 0,
                name: boarding.service?.serviceName || 'Boarding Service'
            },
            price: nightlyPrice,
            beforePrice: service?.beforePrice ?? boarding.service?.beforePrice ?? nightlyPrice,
            durationInMinutes: boarding.service?.duration || 0,
            duration: {
                hours: boarding.service?.duration ? Math.trunc(boarding.service.duration / 60) : 0,
                minutes: boarding.service?.duration ? boarding.service.duration % 60 : 0
            },
            discount: {
                type: 'amount',
                value: 0
            },
            roomUnitId: boarding.roomUnitId,
            room: boarding.room,
            feedings: feedings,
            medications: medications,
            serviceItems: boarding.service?.serviceItems,
            businessArea: 'boarding',
            color: boarding.service?.serviceColor ?? '',
            notes: boarding.notes,
            saveCustomPrice: selectedOffer == null ? boarding.saveCustomPrice : false,
            customPriceId: selectedOffer == null ? boarding.customPriceId : undefined,
            customPriceAmount:
                selectedOffer == null && Number(boarding.service?.beforePrice) > 0
                    ? boarding.service?.beforePrice
                    : undefined,
            ...(selectedOffer != null ? { selectedOffer } : {})
        };
        if (selectedOffer == null) {
            const boardingEnrollment = enrollmentFromFlowPayload(boarding, service);
            if (shouldApplyEnrollmentForDate(boardingEnrollment, boarding?.from)) {
                boardingToService.enrollment = boardingEnrollment;
            }
            const membershipItem = boarding.service?.membershipItem ?? service?.membershipItem;
            if (membershipItem != null) {
                boardingToService.membershipItem = membershipItem;
            }
        } else {
            boardingToService.enrollment = undefined;
            boardingToService.membershipItem = undefined;
        }

        const petFounded = multipleAppointments.findIndex((app: AppointmentMultiple) => app.petId === petId);
        if (petFounded !== -1) {
            const newMultipleAppointments = [...multipleAppointments];
            if (versionPetAndService === versionPetAndServices.edit) {
                const serviceFounded = multipleAppointments[petFounded]?.variableData?.findIndex(
                    (app: DataVaraible) => app.service?.service?.id === service?.service?.id
                );
                if (serviceFounded !== -1) {
                    newMultipleAppointments[petFounded].variableData[serviceFounded] = {
                        service: boardingToService,
                        staffId: boarding.room?.staffIds?.[0],
                        date: boarding?.from || date,
                        dateEnd: boarding?.to || date.clone().add(boarding.service?.durationService || 0, 'minutes'),
                        itemData: boardingItemData
                    };
                    setMultipleAppointments(newMultipleAppointments);
                } else {
                    newMultipleAppointments[petFounded] = {
                        ...newMultipleAppointments[petFounded],
                        variableData: [
                            ...newMultipleAppointments[petFounded].variableData,
                            {
                                service: boardingToService,
                                staffId: boarding.room?.staffIds?.[0],
                                date: boarding?.from || date,
                                dateEnd:
                                    boarding?.to || date.clone().add(boarding.service?.durationService || 0, 'minutes'),
                                itemData: boardingItemData
                            }
                        ]
                    };
                    setMultipleAppointments(newMultipleAppointments);
                }
            } else {
                newMultipleAppointments[petFounded] = {
                    ...newMultipleAppointments[petFounded],
                    variableData: [
                        ...newMultipleAppointments[petFounded].variableData,
                        {
                            service: boardingToService,
                            staffId: boarding.room?.staffIds?.[0],
                            date: boarding?.from || date,
                            dateEnd:
                                boarding?.to || date.clone().add(boarding.service?.durationService || 0, 'minutes'),
                            itemData: boardingItemData
                        }
                    ]
                };
                setMultipleAppointments(newMultipleAppointments);
            }
        } else {
            const appointmentToSave: AppointmentMultiple = {
                clientId: parentId!,
                petId: petIdSelected!,
                createdByStaffId: auth.user!.staffId,
                variableData: [
                    {
                        service: boardingToService,
                        staffId: boarding.room?.staffIds?.[0],
                        date: boarding?.from || date,
                        dateEnd: boarding?.to || date.clone().add(boarding.service?.durationService || 0, 'minutes'),
                        itemData: boardingItemData
                    }
                ],
                notes: notes
            };
            setMultipleAppointments(prev => [...prev, appointmentToSave]);
        }
    };

    const handlerSaveTransportation = (transportation: Transportation, petIdSelected: number) => {
        setShowPetAndServices(false);
        setShowComplementaryItem(false);
        setVersionPetAndService(undefined);
        backPetAndServicesOrComplementaryItem();
        setType(itemTypes.service);
        const isTransportationEdit = versionPetAndService === versionPetAndServices.edit;
        const transportationWithMembership: Transportation = {
            ...transportation,
            enrollment: enrollmentFromFlowPayload(transportation, service),
            membershipItem: membershipItemFromFlowPayload(transportation, service)
        };
        const petFounded = multipleAppointments.findIndex((app: AppointmentMultiple) => app.petId === petId);
        const membershipUsesAlreadyAllocated =
            !isTransportationEdit && petFounded !== -1 && transportationWithMembership.enrollment?.id != null
                ? new Set(
                      (multipleAppointments[petFounded]?.variableData ?? [])
                          .filter(
                              entry =>
                                  entry.itemData?.flow === 'transportation' &&
                                  entry.service?.enrollment?.id === transportationWithMembership.enrollment?.id &&
                                  shouldApplyMembershipRedeemOnSave(entry.service)
                          )
                          .map(entry => entry.date?.clone().tz(timezone).startOf('day').format('YYYY-MM-DD'))
                          .filter((day): day is string => Boolean(day))
                  ).size
                : 0;
        const editLegType: TransportationLegType | undefined = isTransportationEdit
            ? multipleAppointments[petFounded]?.variableData?.find(
                  entry => entry.service?.service?.id === service?.service?.id
              )?.itemData?.type === 'dropoff'
                ? 'dropoff'
                : 'pickup'
            : undefined;
        const newTransportationVariableData = buildTransportationVariableDataPerSlot(
            transportationWithMembership,
            date,
            timezone,
            isTransportationEdit,
            membershipUsesAlreadyAllocated,
            editLegType,
            {
                // Prefer leg catalogs; service.beforePrice is only the pickup wrapper from membership preload.
                pickup: transportation.service?.pickupPrice ?? service?.beforePrice,
                dropoff: transportation.service?.dropoffPrice ?? service?.beforePrice
            }
        );

        if (petFounded !== -1) {
            const newMultipleAppointments = [...multipleAppointments];
            if (isTransportationEdit) {
                const editDate =
                    dateToEdit ??
                    (editLegType === 'dropoff' ? transportation.dropOff?.time.from : transportation.pickup?.time.from);
                const serviceFounded = findTransportationVariableDataIndexForEdit(
                    multipleAppointments[petFounded]?.variableData ?? [],
                    service?.service?.id,
                    editDate,
                    editLegType
                );
                if (serviceFounded !== -1 && newTransportationVariableData[0]) {
                    newMultipleAppointments[petFounded].variableData[serviceFounded] = newTransportationVariableData[0];
                    setMultipleAppointments(newMultipleAppointments);
                }
            } else {
                newMultipleAppointments[petFounded] = {
                    ...newMultipleAppointments[petFounded],
                    variableData: [
                        ...newMultipleAppointments[petFounded].variableData,
                        ...newTransportationVariableData
                    ]
                };
                setMultipleAppointments(newMultipleAppointments);
            }
        } else {
            const appointmentToSave: AppointmentMultiple = {
                clientId: parentId!,
                petId: petIdSelected!,
                createdByStaffId: auth.user!.staffId,
                variableData: newTransportationVariableData,
                notes: notes
            };
            setMultipleAppointments(prev => [...prev, appointmentToSave]);
        }
    };

    const handlerSaveTraining = (training: Training, petIdSelected: number) => {
        setShowPetAndServices(false);
        setShowComplementaryItem(false);
        setVersionPetAndService(undefined);
        backPetAndServicesOrComplementaryItem();
        setType(itemTypes.service);
        const isTrainingEdit = versionPetAndService === versionPetAndServices.edit;
        const trainingWithMembership: Training = {
            ...training,
            enrollment: enrollmentFromFlowPayload(training, service),
            membershipItem: membershipItemFromFlowPayload(training, service)
        };
        const petFounded = multipleAppointments.findIndex((app: AppointmentMultiple) => app.petId === petId);
        const membershipUsesAlreadyAllocated =
            !isTrainingEdit && petFounded !== -1 && trainingWithMembership.enrollment?.id != null
                ? (multipleAppointments[petFounded]?.variableData ?? []).filter(
                      entry =>
                          entry.itemData?.flow === 'training' &&
                          entry.service?.enrollment?.id === trainingWithMembership.enrollment?.id &&
                          shouldApplyMembershipRedeemOnSave(entry.service)
                  ).length
                : 0;
        const serviceId =
            trainingWithMembership.service?.serviceId ?? service?.service?.id ?? training.service?.serviceId;
        const catalogFromPetServices = petServices.find(ps => ps.service.id === serviceId)?.price;
        const catalogOverride = service?.beforePrice ?? trainingWithMembership.service?.price ?? catalogFromPetServices;
        const newTrainingVariableData = buildTrainingVariableDataPerSlot(
            trainingWithMembership,
            date,
            timezone,
            isTrainingEdit,
            membershipUsesAlreadyAllocated,
            catalogOverride
        );

        if (petFounded !== -1) {
            const newMultipleAppointments = [...multipleAppointments];
            if (isTrainingEdit) {
                const editDate = dateToEdit ?? training.from;
                const serviceFounded = findTrainingVariableDataIndexForEdit(
                    multipleAppointments[petFounded]?.variableData ?? [],
                    service?.service?.id,
                    editDate
                );
                if (serviceFounded !== -1) {
                    newMultipleAppointments[petFounded].variableData[serviceFounded] = newTrainingVariableData[0];
                    setMultipleAppointments(newMultipleAppointments);
                }
            } else {
                newMultipleAppointments[petFounded] = {
                    ...newMultipleAppointments[petFounded],
                    variableData: [...newMultipleAppointments[petFounded].variableData, ...newTrainingVariableData]
                };
                setMultipleAppointments(newMultipleAppointments);
            }
        } else {
            const appointmentToSave: AppointmentMultiple = {
                clientId: parentId!,
                petId: petIdSelected!,
                createdByStaffId: auth.user!.staffId,
                variableData: newTrainingVariableData,
                notes: notes
            };
            setMultipleAppointments(prev => [...prev, appointmentToSave]);
        }
    };

    const handlerSaveWalking = (walking: Walking, petIdSelected: number) => {
        setShowPetAndServices(false);
        setShowComplementaryItem(false);
        setVersionPetAndService(undefined);
        backPetAndServicesOrComplementaryItem();
        setType(itemTypes.service);
        const isWalkingEdit = versionPetAndService === versionPetAndServices.edit;
        const walkingWithMembership: Walking = {
            ...walking,
            enrollment: enrollmentFromFlowPayload(walking, service),
            membershipItem: membershipItemFromFlowPayload(walking, service)
        };
        const petFounded = multipleAppointments.findIndex((app: AppointmentMultiple) => app.petId === petId);
        const membershipUsesAlreadyAllocated =
            !isWalkingEdit && petFounded !== -1 && walkingWithMembership.enrollment?.id != null
                ? (multipleAppointments[petFounded]?.variableData ?? []).filter(
                      entry =>
                          entry.itemData?.flow === 'walking' &&
                          entry.service?.enrollment?.id === walkingWithMembership.enrollment?.id &&
                          shouldApplyMembershipRedeemOnSave(entry.service)
                  ).length
                : 0;
        const serviceId =
            walkingWithMembership.service?.serviceId ?? service?.service?.id ?? walking.service?.serviceId;
        const catalogFromPetServices = petServices.find(ps => ps.service.id === serviceId)?.price;
        const catalogOverride = service?.beforePrice ?? walkingWithMembership.service?.price ?? catalogFromPetServices;
        const newWalkingVariableData = buildWalkingVariableDataPerSlot(
            walkingWithMembership,
            date,
            timezone,
            isWalkingEdit,
            membershipUsesAlreadyAllocated,
            catalogOverride
        );

        if (petFounded !== -1) {
            const newMultipleAppointments = [...multipleAppointments];
            if (isWalkingEdit) {
                const editDate = dateToEdit ?? walking.from;
                const serviceFounded = findWalkingVariableDataIndexForEdit(
                    multipleAppointments[petFounded]?.variableData ?? [],
                    service?.service?.id,
                    editDate
                );
                if (serviceFounded !== -1) {
                    newMultipleAppointments[petFounded].variableData[serviceFounded] = newWalkingVariableData[0];
                    setMultipleAppointments(newMultipleAppointments);
                }
            } else {
                newMultipleAppointments[petFounded] = {
                    ...newMultipleAppointments[petFounded],
                    variableData: [...newMultipleAppointments[petFounded].variableData, ...newWalkingVariableData]
                };
                setMultipleAppointments(newMultipleAppointments);
            }
        } else {
            const appointmentToSave: AppointmentMultiple = {
                clientId: parentId!,
                petId: petIdSelected!,
                createdByStaffId: auth.user!.staffId,
                variableData: newWalkingVariableData,
                notes: notes
            };
            setMultipleAppointments(prev => [...prev, appointmentToSave]);
        }
    };

    const handlerRemoveAppointments = (petId: number | undefined, service: PetServiceWrapper | undefined) => {
        setShowPetAndServices(false);
        setShowComplementaryItem(false);
        setVersionPetAndService(undefined);
        backPetAndServicesOrComplementaryItem();
        const petFounded = multipleAppointments.findIndex((app: AppointmentMultiple) => app.petId === petId);
        if (petFounded !== -1) {
            const appointmentsWithoutApptRemoved = multipleAppointments[petFounded]?.variableData?.filter(
                (appt: DataVaraible) => appt.service?.service.id !== service?.service.id
            );
            const newMultipleAppointments = [...multipleAppointments];
            newMultipleAppointments[petFounded] = {
                ...newMultipleAppointments[petFounded],
                variableData: appointmentsWithoutApptRemoved
            };

            if (appointmentsWithoutApptRemoved?.length === 0) {
                const appointmentsWithoutThisPet = newMultipleAppointments.filter(
                    (app: AppointmentMultiple) => app.petId !== petId
                );
                setMultipleAppointments(appointmentsWithoutThisPet);
            } else {
                setMultipleAppointments(newMultipleAppointments);
            }
        }
    };

    const getIndexOfItem = (array: Array<DataVaraible>) => {
        const items = {
            [itemTypes.service]: 0,
            'add-on': array.findIndex(
                (item: DataVaraible) =>
                    item.addOn?.id === addOn?.id && (!dateToEdit || item.date?.format('L') === dateToEdit.format('L'))
            ),
            [itemTypes.product]: array.findIndex(
                (item: DataVaraible) => item.product?.businessProduct?.id === product?.businessProduct?.id
            ),
            [itemTypes.custom]: array.findIndex((item: DataVaraible) => item.customDescription === custom)
        };
        return items[type as itemTypes] ?? -1;
    };

    const buildItemToSave = (
        date?: Moment,
        petId?: number,
        selectedProduct?: Product,
        selectedAddOn?: AddOnService,
        custom?: string,
        staffId?: number,
        discount?: Discount,
        quantity?: number,
        amount?: number,
        taxId?: number,
        dates?: Array<Moment>
    ) => {
        setShowPetAndServices(false);
        setShowComplementaryItem(false);
        setVersionPetAndService(undefined);
        backPetAndServicesOrComplementaryItem();
        if (versionPetAndService === versionPetAndServices.add) {
            const petFounded = multipleAppointments.findIndex((app: AppointmentMultiple) => app.petId === petId);
            if (petFounded !== -1) {
                const datesToSave = dates && dates.length > 0 ? dates : date ? [date] : [];
                const newItems = datesToSave.map(dateItem => ({
                    date: dateItem,
                    staffId: staffId,
                    product: type === itemTypes.product ? selectedProduct : undefined,
                    addOn: type === itemTypes.addOn ? selectedAddOn : undefined,
                    customDescription: type === itemTypes.custom ? custom : undefined,
                    itemData: {
                        discount: {
                            type: discount?.type || 'amount',
                            value: discount?.value ?? 0
                        },
                        quantity: quantity,
                        price: amount,
                        total: quantity && amount ? parseFloat((quantity * amount).toFixed(2)) : 0,
                        taxId: taxId
                    }
                }));
                const newMultipleAppointments = [...multipleAppointments];
                newMultipleAppointments[petFounded] = {
                    ...newMultipleAppointments[petFounded],
                    variableData: [...newMultipleAppointments[petFounded].variableData, ...newItems]
                };
                setMultipleAppointments(newMultipleAppointments);
            }
        } else if (versionPetAndService === versionPetAndServices.edit) {
            const petFounded = multipleAppointments.findIndex((app: AppointmentMultiple) => app.petId === petId);
            if (petFounded !== -1) {
                const newMultipleAppointments = [...multipleAppointments];
                const appt: AppointmentMultiple = newMultipleAppointments[petFounded];
                const elementEdited: DataVaraible = {
                    date: date!,
                    staffId: staffId,
                    product: type === itemTypes.product ? selectedProduct : undefined,
                    addOn: type === itemTypes.addOn ? selectedAddOn : undefined,
                    customDescription: type === itemTypes.custom ? custom : undefined,
                    itemData: {
                        discount: {
                            type: discount?.type || 'amount',
                            value: discount?.value ?? 0
                        },
                        quantity: quantity,
                        price: amount,
                        total: quantity && amount ? parseFloat((quantity * amount).toFixed(2)) : 0,
                        taxId: taxId
                    }
                };
                const index = getIndexOfItem(appt.variableData);
                if (index !== -1) {
                    newMultipleAppointments[petFounded].variableData[index] = elementEdited;
                }
                if (newMultipleAppointments[petFounded]?.variableData.length === 0) {
                    const appointmentsWithoutThisPet = newMultipleAppointments.filter(
                        (app: AppointmentMultiple) => app.petId !== petId
                    );
                    setMultipleAppointments(appointmentsWithoutThisPet);
                } else {
                    setMultipleAppointments(newMultipleAppointments);
                }
            }
        }
    };

    const handlerSaveItems = (
        date?: Moment,
        petId?: number,
        selectedProduct?: Product,
        selectedAddOn?: AddOnService,
        custom?: string,
        staffId?: number,
        discount?: Discount,
        quantity?: number,
        amount?: number,
        taxId?: number,
        dates?: Array<Moment>
    ) => {
        buildItemToSave(
            date,
            petId,
            selectedProduct,
            selectedAddOn,
            custom,
            staffId,
            discount,
            quantity,
            amount,
            taxId,
            dates
        );
    };

    const getArrayWithItemsRemoved = (array: Array<DataVaraible>, dateToRemove?: Moment) => {
        const items = {
            [itemTypes.service]: array,
            'add-on': array.filter(
                (item: DataVaraible) =>
                    item.addOn?.id !== addOn?.id || (item.addOn?.id === addOn?.id && item.date !== dateToRemove)
            ),
            [itemTypes.product]: array.filter(
                (item: DataVaraible) => item.product?.businessProduct?.id !== product?.businessProduct?.id
            ),
            [itemTypes.custom]: array.filter((item: DataVaraible) => item.customDescription !== custom)
        };
        return items[type as itemTypes] ?? array;
    };

    const handlerRemoveItems = (dateToRemove?: Moment) => {
        setShowPetAndServices(false);
        setShowComplementaryItem(false);
        setVersionPetAndService(undefined);
        backPetAndServicesOrComplementaryItem();
        const petIndex = multipleAppointments.findIndex((app: AppointmentMultiple) => app.petId === petId);
        if (petIndex !== -1) {
            const appointmentsWithoutApptRemoved = getArrayWithItemsRemoved(
                multipleAppointments[petIndex].variableData,
                dateToRemove
            );
            const newMultipleAppointments = [...multipleAppointments];
            newMultipleAppointments[petIndex] = {
                ...newMultipleAppointments[petIndex],
                variableData: appointmentsWithoutApptRemoved
            };
            if (appointmentsWithoutApptRemoved?.length === 0) {
                const appointmentsWithoutThisPet = newMultipleAppointments.filter(
                    (app: AppointmentMultiple) => app.petId !== petId
                );
                setMultipleAppointments(appointmentsWithoutThisPet);
            } else {
                setMultipleAppointments(newMultipleAppointments);
            }
        }
    };

    const handlerParentID = (petParentId: number, clientName?: string) => {
        if (petParentId !== 0) {
            if (parentId !== petParentId) {
                setService(undefined);
            }
            setParentId(petParentId);
            if (clientName !== undefined) {
                setParentName(clientName);
            }
            dispatch(fetchPetsThunk(apiClientWrapper, petParentId));
        } else {
            handlerRemove(wizardBooking.PetParent);
            handlerRemove(wizardBooking.Service);
            handlerRemove(wizardBooking.Staff);
            handlerRemove(wizardBooking.Deposit);
            handlerRemove(wizardBooking.Notes);
            props.onRemoveClient && props.onRemoveClient();
        }
    };

    const addNewClient = () => {
        props.onAddClient && props.onAddClient();
    };

    const backHandler = () => {
        setSaving(true);
        setDepositCollect(false);
        setDepositPaidAmount(0);
        setOrderData(prev => ({ ...prev, showDetail: false }));
        dispatch(deleteBookingThunk(apiClientWrapper, savedBooking!.id!, true));
    };

    const backPetAndServicesOrComplementaryItem = () => {
        // If we came in with a membership intent, apply it only the first time we enter PetAndServices.
        setMembershipPrefillEnabled(false);
        setPetId(undefined);
        setFlow(undefined);
        setDateToEdit(undefined);
        setStaffId(undefined);
        setDiscount({
            type: 'amount',
            value: 0
        });
        setTaxId(undefined);
        setShowPetAndServices(false);
        setShowComplementaryItem(false);
        setVersionPetAndService(undefined);
        setService(undefined);
        setIsEvaluationActive(props.isEvaluation || false);
    };

    const handlerCollected = (paidAmount: number) => {
        setDepositPaidAmount(paidAmount);
        setDepositCollect(false);
        setDepositSkipped(false);
        setAppointmentCreated(true);
        props.onChangeNewBooking && props.onChangeNewBooking(false);
        if (savedBooking?.id) {
            dispatch(changeBookingStatusThunk(apiClientWrapper, savedBooking.id, AppointmentStatus.BOOKED));
        }
    };

    const handlerSkipAndBook = () => {
        setDepositCollect(false);
        setDepositSkipped(true);
        setAppointmentCreated(true);
        props.onChangeNewBooking && props.onChangeNewBooking(false);
        if (savedBooking?.id) {
            dispatch(changeBookingStatusThunk(apiClientWrapper, savedBooking.id, AppointmentStatus.BOOKED));
        }
    };

    const changeTabHandler = () => {
        if (touched) {
            setClosing(prev => ({ ...prev, tabChange: true }));
        } else {
            props.onShowBlockedTime && props.onShowBlockedTime();
        }
    };

    const handleClose = (value?: boolean) => {
        closing.close && handleCloseBooking(value);
        closing.tabChange && props.onShowBlockedTime && props.onShowBlockedTime();
    };

    const handleClosing = () => {
        if (recurringSeriesCreated) {
            handleBookedBooking();
            return;
        }
        setClosing(prev => ({
            ...prev,
            close: true
        }));
        setOrderData(prev => ({ ...prev, showDetail: false }));
    };

    const handleOpenRecurring = () => {
        setIsCollectDeposit(false);
        setRecurringConfig(prev => prev ?? DEFAULT_RECURRING_SERIES_CONFIG);
        setShowRecurringPanel(true);
    };

    const handleCloseRecurring = () => {
        setShowRecurringPanel(false);
        setRecurringConfig(undefined);
        if (recurringSeriesCreated) {
            handleBookedBooking();
        }
    };

    const handleSeriesCreated = (created: RecurringSeriesCreated) => {
        setRecurringSeriesCreated(created);
        props.onChangeNewBooking && props.onChangeNewBooking(false);
    };

    const handleChangeDeposit = (value: boolean) => {
        setIsCollectDeposit(value);
        if (value) {
            handleCloseRecurring();
        }
    };

    const handlerAddAnotherPet = () => {
        setVersionPetAndService(versionPetAndServices.another);
        setFlow(undefined);
        setService(undefined);
        setDateToEdit(undefined);
        setShowPetAndServices(true);
    };

    const handlerAddItem = (value: number, type?: itemTypes) => {
        setType(type);
        setPetId(value);
        setTaxId(type === itemTypes.custom ? getDefaultCustomItemsTaxId(marketplace) : undefined);
        setVersionPetAndService(versionPetAndServices.add);
        if (type === itemTypes.service) {
            setFlow(undefined);
            setService(undefined);
            setDateToEdit(undefined);
            setShowPetAndServices(true);
        } else {
            setShowComplementaryItem(true);
        }
    };

    const handlerDeleteAppointmentsPet = (value: number) => {
        const multiplePetFiltered: Array<AppointmentMultiple> = multipleAppointments.filter(
            (appt: AppointmentMultiple) => appt.petId !== value
        );
        setMultipleAppointments(multiplePetFiltered);
    };

    const isServiceEvaluation = (serviceId: string | number) => {
        if (!multipleAppointments?.length) return false;

        return multipleAppointments.some(app =>
            app?.variableData?.some(vd => {
                const innerService = vd?.service?.service;

                return (
                    innerService?.id === serviceId && Object.prototype.hasOwnProperty.call(innerService, 'evaluation')
                );
            })
        );
    };

    const handlerEditAppointmentPet = (
        petId: number,
        dateEdit: Moment,
        staffId?: number,
        itemData?: ItemData,
        service?: PetServiceWrapper,
        product?: Product,
        addOn?: AddOnService,
        custom?: string,
        type?: itemTypes,
        dateEnd?: Moment
    ) => {
        setPetId(petId);
        setStaffId(staffId);
        setDateToEdit(dateEdit);
        setDateEnd(dateEnd);
        setDiscount(itemData?.discount!);
        setTaxId(itemData?.taxId);
        if (service) {
            if (isServiceEvaluation(service.service.id)) {
                setIsEvaluationActive(true);
            }
            setService(service);
            setShowPetAndServices(true);
        } else {
            setShowComplementaryItem(true);
            if (product) {
                setProduct(product);
            } else if (addOn) {
                setAddOn(addOn);
            } else {
                setCustom(custom);
            }
            setQuantity(itemData?.quantity || 1);
            setAmount(itemData?.price || 0);
        }
        setType(type);
        setVersionPetAndService(versionPetAndServices.edit);
        setFlow(itemData?.flow);
        if (itemData?.flow === 'transportation') {
            setTransportationType(itemData?.type === 'pickup' ? 'pickup' : 'dropoff');
        }
        setShowActions({
            date: undefined,
            staffId: undefined,
            service: undefined,
            petId: undefined,
            product: undefined,
            addOn: undefined,
            custom: undefined,
            type: undefined,
            discount: undefined,
            itemData: undefined,
            dateEnd: undefined
        });
    };

    const getArrayWithItemRemoved = (
        array: Array<DataVaraible>,
        type: itemTypes,
        value?: string | number,
        dateToRemove?: Moment
    ) => {
        const items = {
            [itemTypes.service]: array.filter((appt: DataVaraible) => {
                const matchesService = appt.service?.service.id === value;
                if (!matchesService) return true;
                if (dateToRemove) {
                    if (!appt.date) return true;
                    return !appt.date.isSame(dateToRemove);
                }
                return false;
            }),
            'add-on': array.filter(
                (appt: DataVaraible) =>
                    appt.addOn?.id !== value ||
                    (appt.addOn?.id === value &&
                        (dateToRemove ? !appt.date?.isSame?.(dateToRemove) : appt.date !== dateToRemove))
            ),
            [itemTypes.product]: array.filter((appt: DataVaraible) => appt.product?.id !== value),
            [itemTypes.custom]: array.filter((appt: DataVaraible) => appt.customDescription !== value)
        };
        return items[type];
    };

    const handlerDeleteAppointmentPet = (
        petId: number,
        value?: number | string,
        type?: itemTypes,
        dateToRemove?: Moment
    ) => {
        const petIndex = multipleAppointments.findIndex((app: AppointmentMultiple) => app.petId === petId);
        if (petIndex !== -1) {
            const appointmentsWithoutApptRemoved = getArrayWithItemRemoved(
                multipleAppointments[petIndex].variableData,
                type!,
                value,
                dateToRemove
            );
            const newMultipleAppointments = [...multipleAppointments];
            newMultipleAppointments[petIndex] = {
                ...newMultipleAppointments[petIndex],
                variableData: appointmentsWithoutApptRemoved
            };
            if (appointmentsWithoutApptRemoved?.length === 0) {
                const appointmentsWithoutThisPet = newMultipleAppointments.filter(
                    (app: AppointmentMultiple) => app.petId !== petId
                );
                setMultipleAppointments(appointmentsWithoutThisPet);
            } else {
                setMultipleAppointments(newMultipleAppointments);
            }
            setShowActions({
                date: undefined,
                staffId: undefined,
                service: undefined,
                petId: undefined,
                product: undefined,
                addOn: undefined,
                custom: undefined,
                type: undefined,
                discount: undefined,
                itemData: undefined,
                dateEnd: undefined
            });
            setLoadingAppointments(true);
        }
    };

    const getMultipleBookingToSave = (): Array<NewBookingAppointment> => {
        const multipleAppointmentsToSave: Array<NewBookingAppointment> = [];

        multipleAppointments.forEach((appt: AppointmentMultiple) => {
            appt.variableData.forEach((data: DataVaraible) => {
                if (data.service && data.date) {
                    const roomUnitId =
                        data.service.roomUnitId ??
                        getRoomUnitId(data.service.room, data.itemData?.flow, data.service.roomUnitId);
                    // Daycare and Boarding are room-based services and are not linked to a staff member.
                    // Avoid sending staff_id for these flows.
                    const isRoomBasedService = data.itemData?.flow === 'daycare' || data.itemData?.flow === 'boarding';
                    const isPickUp = data.itemData?.type === 'pickup';
                    const dateToSave = data.date.clone().seconds(0).milliseconds(0);
                    const dateEndToSave = data.dateEnd
                        ? data.dateEnd.clone().seconds(0).milliseconds(0)
                        : dateToSave.clone().add(data.service?.durationInMinutes || 0, 'minutes');
                    const enrollmentId = shouldApplyMembershipRedeemOnSave(data.service)
                        ? data.service.enrollment?.id
                        : undefined;

                    multipleAppointmentsToSave.push({
                        petId: appt.petId,
                        serviceId: data.service?.service.id!,
                        orderItemPrice: resolveOrderItemPriceForSave(data.service),
                        date: dateToSave,
                        notes: notes || '',
                        createdByStaffId: appt.createdByStaffId,
                        clientId: appt.clientId,
                        staffId: isRoomBasedService ? undefined : data.staffId,
                        dateEnd: dateEndToSave,
                        tasksToSave: getTasksToSave(data, roomUnitId),
                        roomUnitId: roomUnitId,
                        ...(enrollmentId != null
                            ? {
                                  redeemType: 'Enrollment',
                                  redeemId: enrollmentId
                              }
                            : {}),
                        discount: resolveDiscountPayloadForSave(data.itemData?.discount, data.service),
                        ...(data.itemData?.flow === 'walking' || data.itemData?.flow === 'training'
                            ? { serviceSessionId: data.service.serviceSessionId ?? null }
                            : {}),
                        details:
                            data.itemData?.flow === 'transportation'
                                ? {
                                      transportation: {
                                          type: isPickUp ? 'pickup' : 'dropoff',
                                          vehicleId: data.service.vehicle?.id || 0,
                                          address: data.service.address,
                                          instructions: data.service.instructions
                                      }
                                  }
                                : data.itemData?.flow === 'walking'
                                ? {
                                      walking: {
                                          type: data.service.inHomeService ? 'in_home' : 'brick_and_mortar',
                                          address: data.service.address,
                                          duration: data.service.durationInMinutes,
                                          instructions: data.service.instructions
                                      }
                                  }
                                : data.itemData?.flow === 'training'
                                ? {
                                      training: {
                                          type: data.service.inHomeService ? 'in_home' : 'brick_and_mortar',
                                          address: data.service.address,
                                          duration: data.service.durationInMinutes,
                                          instructions: data.service.instructions,
                                          sessionType: resolveBookingType(data.service.bookingType)
                                      }
                                  }
                                : data.itemData?.flow === 'boarding'
                                ? {
                                      boarding: {
                                          room: data.service.room?.id,
                                          notes: data.service.notes,
                                          ...(data.service.selectedOffer != null
                                              ? { selectedOffer: data.service.selectedOffer }
                                              : {})
                                      }
                                  }
                                : undefined
                    });
                }
            });
        });

        return multipleAppointmentsToSave;
    };

    const getType = (data: DataVaraible) => {
        let type = itemTypes.service;
        if (data.service) {
            type = itemTypes.service;
        } else if (data.product) {
            type = itemTypes.product;
        } else if (data.addOn) {
            type = itemTypes.addOn;
        } else if (data.customDescription) {
            type = itemTypes.custom;
        }
        return type;
    };

    const getOrderizable = (data: DataVaraible): NewBookingOrderizable => {
        const items = {
            [itemTypes.service]: {
                id: data.service?.service.id,
                type: OrderizableTypes.APPOINTMENT
            },
            'add-on': {
                id: data.addOn?.id,
                type: OrderizableTypes.ADDON_SERVICE
            },
            [itemTypes.product]: {
                id: data.product?.businessProduct?.id,
                type: OrderizableTypes.PRODUCT
            },
            [itemTypes.custom]: {
                id: undefined,
                type: undefined
            }
        };
        return items[getType(data)] || undefined;
    };

    const getDescription = (data: DataVaraible) => {
        const items = {
            [itemTypes.service]: data.service?.service?.name,
            'add-on': data.addOn?.name,
            [itemTypes.product]: data.product?.name,
            [itemTypes.custom]: data.customDescription
        };
        return items[getType(data)] || undefined;
    };

    const getComplementaryItemsToSave = (): NewBookingOrder => {
        const items: Array<NewBookingOrderItems> = [];

        multipleAppointments.forEach((appt: AppointmentMultiple) => {
            appt.variableData.forEach((data: DataVaraible) => {
                if (!data.service) {
                    items.push({
                        orderizable: getOrderizable(data),
                        staffId: data.staffId,
                        description: getDescription(data),
                        petId: appt.petId,
                        quantity: data.itemData?.quantity || 0,
                        amount: data.itemData?.price?.toString() || '0',
                        taxId: data.itemData?.taxId,
                        startedAt: data.date ? data.date.clone().seconds(0).milliseconds(0) : undefined,
                        discount: {
                            percentage:
                                data.itemData?.discount?.type === 'percentage'
                                    ? data.itemData?.discount?.value
                                    : undefined,
                            amount:
                                data.itemData?.discount?.type === 'amount'
                                    ? data.itemData?.discount?.value?.toString()
                                    : undefined
                        }
                    });
                }
            });
        });

        return {
            customerId: multipleAppointments[0].clientId,
            shipTocustomerId: multipleAppointments[0].clientId,
            notes: '',
            issuedAt: moment().tz(timezone).seconds(0).milliseconds(0),
            items: items
        };
    };

    const getTasksToSave = (variableData: DataVaraible, roomUnitId?: number): Array<Task> => {
        const items: Array<Task> = [];
        const { service, date, dateEnd, itemData } = variableData;
        const flow = itemData?.flow;

        const isMultiDay = flow === 'boarding' && dateEnd && dateEnd.isAfter(date);

        const createFeedingTasksForDay = (dayLocal: Moment) => {
            service?.feedings?.forEach((feeding: Feeding) => {
                const legacySpecificOnly =
                    (!feeding.momentTypes || feeding.momentTypes.length === 0) &&
                    !!feeding.specificMomentTimes &&
                    Object.keys(feeding.specificMomentTimes).length > 0;

                const pushPresetForMoment = (momentTypeId: number) => {
                    const taskDate = createTaskDate(
                        dayLocal,
                        momentTypeId === 1 ? morning : momentTypeId === 2 ? afternoon : evening,
                        timezone
                    );
                    const rangeDate = getRangeDateByMomentTypeIdWithTimezone(
                        dayLocal,
                        momentTypeId,
                        morning,
                        afternoon,
                        evening,
                        timezone
                    );

                    if (isMultiDay) {
                        if (!shouldCreateTaskForDay(taskDate, date, dateEnd!, rangeDate.from, rangeDate.to)) {
                            return;
                        }
                    }

                    items.push({
                        taskDate,
                        feeding: {
                            taskType: 1,
                            feedingMomentTypeId: momentTypeId,
                            foodProvisionerTypeId: feeding.foodProvisionType,
                            foodTypeId: feeding.foodType,
                            foodQuantity: feeding.quantity?.quantity?.toString(),
                            foodQuantityUnitId: feeding.quantity?.unit,
                            feedingInstructions: feeding.instructions,
                            feedingBrand: feeding.brand,
                            roomUnitId,
                            specificTime: false
                        }
                    } as Task);
                };

                if (feeding.feedingScheduleType === 'specificTime' && feeding.momentTypes?.length) {
                    const anySelectedHasTime = feeding.momentTypes.some(
                        m => !!String(feeding.specificMomentTimes?.[m] ?? '').trim()
                    );
                    if (anySelectedHasTime) {
                        feeding.momentTypes.forEach((momentTypeId: number) => {
                            const timeString = feeding.specificMomentTimes?.[momentTypeId];
                            if (!timeString || !String(timeString).trim()) return;
                            const taskDate = createMedicationTaskDate(dayLocal, String(timeString).trim(), timezone);

                            if (isMultiDay) {
                                if (taskDate.isBefore(date) || taskDate.isSameOrAfter(dateEnd!)) {
                                    return;
                                }
                            }

                            items.push({
                                taskDate,
                                feeding: {
                                    taskType: 1,
                                    feedingMomentTypeId: momentTypeId,
                                    foodProvisionerTypeId: feeding.foodProvisionType,
                                    foodTypeId: feeding.foodType,
                                    foodQuantity: feeding.quantity?.quantity?.toString(),
                                    foodQuantityUnitId: feeding.quantity?.unit,
                                    feedingInstructions: feeding.instructions,
                                    feedingBrand: feeding.brand,
                                    roomUnitId,
                                    specificTime: true
                                }
                            } as Task);
                        });
                    } else {
                        feeding.momentTypes.forEach(pushPresetForMoment);
                    }
                } else if (legacySpecificOnly) {
                    Object.entries(feeding.specificMomentTimes!).forEach(([momentTypeIdStr, timeString]) => {
                        const momentTypeId = Number(momentTypeIdStr);
                        if (!timeString || !String(timeString).trim()) return;
                        const taskDate = createMedicationTaskDate(dayLocal, String(timeString).trim(), timezone);

                        if (isMultiDay) {
                            if (taskDate.isBefore(date) || taskDate.isSameOrAfter(dateEnd!)) {
                                return;
                            }
                        }

                        items.push({
                            taskDate,
                            feeding: {
                                taskType: 1,
                                feedingMomentTypeId: momentTypeId,
                                foodProvisionerTypeId: feeding.foodProvisionType,
                                foodTypeId: feeding.foodType,
                                foodQuantity: feeding.quantity?.quantity?.toString(),
                                foodQuantityUnitId: feeding.quantity?.unit,
                                feedingInstructions: feeding.instructions,
                                feedingBrand: feeding.brand,
                                roomUnitId,
                                specificTime: true
                            }
                        } as Task);
                    });
                } else {
                    feeding.momentTypes?.forEach(pushPresetForMoment);
                }
            });
        };

        const createMedicationTasksForDay = (dayLocal: Moment) => {
            service?.medications?.forEach((medication: Medication) => {
                medication.schedule?.forEach((scheduleTime: string) => {
                    const taskDate = createMedicationTaskDate(dayLocal, scheduleTime, timezone);

                    if (isMultiDay) {
                        if (taskDate.isBefore(date) || taskDate.isSameOrAfter(dateEnd)) {
                            return;
                        }
                    }

                    items.push({
                        taskDate,
                        medication: {
                            taskType: 2,
                            medicationName: medication.name,
                            medicationMethodId: medication.methodType,
                            dosageAmount: medication.dosage?.quantity?.toString(),
                            dosageUnitId: medication.dosage?.unit,
                            medicationInstructions: medication.instructions,
                            roomUnitId
                        }
                    });
                });
            });
        };

        const createSpecificMedicationTasks = () => {
            service?.medications?.forEach((medication: Medication) => {
                medication.specificSchedule?.forEach((scheduleTime: DaySchedule) => {
                    if (scheduleTime.enabled) {
                        scheduleTime.doses?.forEach((dose: string) => {
                            const taskDate = createMedicationTaskDate(scheduleTime.date, dose, timezone);

                            items.push({
                                taskDate,
                                medication: {
                                    taskType: 2,
                                    medicationName: medication.name,
                                    medicationMethodId: medication.methodType,
                                    dosageAmount: medication.dosage?.quantity?.toString(),
                                    dosageUnitId: medication.dosage?.unit,
                                    medicationInstructions: medication.instructions,
                                    roomUnitId
                                }
                            });
                        });
                    }
                });
            });
        };

        if (isMultiDay) {
            const start = date.clone().tz(timezone).startOf('day');
            const end = dateEnd.clone().tz(timezone).startOf('day');
            const totalDays = end.diff(start, 'days');

            for (let i = 0; i <= totalDays; i++) {
                const currentLocal = start.clone().add(i, 'days');
                createFeedingTasksForDay(currentLocal);
                createMedicationTasksForDay(currentLocal);
            }
            createSpecificMedicationTasks();
        } else {
            const dayLocal = date.clone().tz(timezone);
            createFeedingTasksForDay(dayLocal);
            createMedicationTasksForDay(dayLocal);
        }

        return items;
    };

    const withoutService = () => {
        let withoutService = true;
        multipleAppointments?.forEach((appt: AppointmentMultiple) => {
            appt.variableData?.forEach((data: DataVaraible) => {
                if (data.service) {
                    withoutService = false;
                }
            });
        });
        return withoutService;
    };

    const getNameItem = (type: itemTypes) => {
        const items = {
            [itemTypes.service]: 'Edit Service',
            'add-on': 'Edit Add On',
            [itemTypes.product]: 'Edit Product',
            [itemTypes.custom]: 'Edit Custom Item'
        };
        return items[type] || '';
    };

    const getItemToDelete = (type: itemTypes) => {
        const items = {
            [itemTypes.service]: showActions.service?.service?.id,
            'add-on': showActions.addOn?.id,
            [itemTypes.product]: showActions.product?.id,
            [itemTypes.custom]: showActions.custom
        };
        return items[type] || '';
    };

    const handlerChangeGeneralDate = (date: Moment) => {
        const normalized = (() => {
            const base = date.clone();
            const remainder = base.minutes() % 15;
            return remainder === 0 ? base : base.add(15 - remainder, 'minutes');
        })();

        setDate(normalized);
        const multipleAppointmentsCopy = [...multipleAppointments];
        multipleAppointmentsCopy.forEach(app => {
            app.variableData?.forEach(data => {
                if (data.itemData?.flow === 'boarding') {
                    data.dateEnd = normalized.clone().add(1, 'days');
                } else {
                    data.dateEnd = normalized.clone().add(data.service?.durationInMinutes || 0, 'minutes');
                }
                data.date = normalized.clone();
            });
        });
        setMultipleAppointments(multipleAppointmentsCopy);
    };

    const getDiscountForVariableData = (data: DataVaraible) => {
        if (data.itemData?.discount?.value) {
            return data.itemData.discount;
        }
        if (data.service?.discount?.value) {
            return data.service.discount;
        }
        return data.itemData?.discount;
    };

    const getPriceWithDiscount = (data: DataVaraible, total?: number) => {
        const discount = getDiscountForVariableData(data);
        if (discount?.type === 'percentage' && total) {
            const amountToDiscount = (discount.value * total) / 100;
            return total - amountToDiscount;
        } else if (discount?.type === 'amount' && total) {
            return total - discount.value;
        }
    };

    const getServicesUsed = () => {
        const servicesUsedAux: Array<number> = [];
        multipleAppointments.forEach(app => {
            if (app.petId === petId) {
                app.variableData?.forEach(data => {
                    if (versionPetAndService === versionPetAndServices.edit) {
                        if (data.service?.service.id !== service?.service.id) {
                            servicesUsedAux.push(data.service?.service.id!);
                        }
                    } else {
                        servicesUsedAux.push(data.service?.service.id!);
                    }
                });
            }
        });
        return servicesUsedAux;
    };

    const getPetsUsed = () => {
        const petsUsedAux: Array<number> = [];
        multipleAppointments.forEach(app => {
            petsUsedAux.push(app.petId);
        });
        return petsUsedAux;
    };

    const getItemsUsed = () => {
        const itemsUsed: Array<number> = [];
        const dates: Array<{
            id?: number;
            date: string;
        }> = [];
        multipleAppointments.forEach(app => {
            if (type === itemTypes.addOn) {
                if (app.petId === petId) {
                    app.variableData?.forEach(data => {
                        if (versionPetAndService === versionPetAndServices.edit) {
                            if (data.addOn?.id && addOn?.id) {
                                if (
                                    data.addOn?.id !== addOn?.id ||
                                    (data.addOn?.id === addOn?.id && data.date?.format('L') && dateToEdit?.format('L'))
                                ) {
                                    itemsUsed.push(data.addOn?.id!);
                                }
                                dates.push({ id: data.addOn?.id, date: data.date?.format('L') });
                            }
                        } else {
                            if (data.addOn?.id) {
                                itemsUsed.push(data.addOn?.id!);
                                dates.push({ id: data.addOn?.id, date: data.date?.format('L') });
                            }
                        }
                    });
                }
            }
            if (type === itemTypes.product) {
                if (app.petId === petId) {
                    app.variableData?.forEach(data => {
                        if (versionPetAndService === versionPetAndServices.edit) {
                            if (data.product?.businessProduct?.id !== product?.businessProduct?.id) {
                                itemsUsed.push(data.product?.businessProduct?.id!);
                            }
                        } else {
                            itemsUsed.push(data.product?.businessProduct?.id!);
                        }
                    });
                }
            }
        });
        setDatesAddOn(dates);
        return itemsUsed;
    };

    const getUniquePetIds = (order: Order): Array<number> => {
        const petIds = new Set<number>();

        order.items.appointments.forEach(item => {
            if (item.pet.id) petIds.add(item.pet.id);
        });
        order.items.addOnServices.forEach(item => {
            if (item.orderItem.petId) petIds.add(item.orderItem.petId);
        });
        order.items.products.forEach(item => {
            if (item.orderItem.petId) petIds.add(item.orderItem.petId);
        });
        order.items.customs.forEach(item => {
            if (item.orderItem.petId) petIds.add(item.orderItem.petId);
        });

        return Array.from(petIds);
    };

    const transformToFeedings = (tasks: Task[], rebook?: boolean): Feeding[] => {
        const feedingTasks = tasks.filter(task => task.feeding?.feedingMomentTypeId);

        const groupedTasks = new Map<string, Task[]>();

        feedingTasks.forEach(task => {
            const key = `${task.feeding?.foodTypeId || ''}-${task.feeding?.foodQuantity || ''}-${
                task.feeding?.foodQuantityUnitId || ''
            }-${task.feeding?.feedingInstructions || ''}`;

            if (!groupedTasks.has(key)) {
                groupedTasks.set(key, []);
            }

            const taskToAdd = rebook ? { ...task, id: undefined } : task;
            groupedTasks.get(key)!.push(taskToAdd);
        });

        return Array.from(groupedTasks.values()).map(taskGroup => {
            const firstTask = taskGroup[0];

            return {
                momentTypes: Array.from(
                    new Set(taskGroup.map(task => task.feeding?.feedingMomentTypeId!).filter(id => id !== undefined))
                ),
                foodProvisionType: firstTask.feeding?.foodProvisionerTypeId!,
                foodType: firstTask.feeding?.foodTypeId,
                quantity: {
                    quantity: firstTask.feeding?.foodQuantity ? parseFloat(firstTask.feeding?.foodQuantity) : undefined,
                    unit: firstTask.feeding?.foodQuantityUnitId
                },
                brand: firstTask.feeding?.feedingBrand,
                instructions: firstTask.feeding?.feedingInstructions
            };
        });
    };

    const transformToMedications = (
        tasks: Task[],
        rebook?: boolean,
        flow?: 'grooming' | 'dental' | 'daycare' | 'boarding',
        stayFrom?: Moment,
        stayTo?: Moment
    ): Medication[] => {
        const medicationTasks = tasks.filter(task => task.medication?.medicationName);

        const groupedTasks = new Map<string, Task[]>();

        medicationTasks.forEach(task => {
            const key = `${task.medication?.medicationName || ''}-${task.medication?.medicationMethodId || ''}-${
                task.medication?.dosageAmount || ''
            }-${task.medication?.dosageUnitId || ''}`;

            if (!groupedTasks.has(key)) {
                groupedTasks.set(key, []);
            }

            const taskToAdd = rebook ? { ...task, id: undefined } : task;
            groupedTasks.get(key)!.push(taskToAdd);
        });

        const medications: Medication[] = [];

        Array.from(groupedTasks.values()).forEach(taskGroup => {
            const firstTask = taskGroup[0];

            if (flow === 'daycare') {
                medications.push({
                    schedule: Array.from(
                        new Set(taskGroup.map(task => task.taskDate?.format('h:mmA') || '').filter(time => time !== ''))
                    ),
                    name: firstTask.medication?.medicationName || '',
                    methodType: firstTask.medication?.medicationMethodId,
                    dosage: {
                        quantity: firstTask.medication?.dosageAmount
                            ? parseFloat(firstTask.medication?.dosageAmount)
                            : undefined,
                        unit: firstTask.medication?.dosageUnitId
                    },
                    instructions: firstTask.medication?.medicationInstructions,
                    specificSchedule: []
                });
                return;
            }

            if (flow === 'boarding' && stayFrom && stayTo) {
                const tasksByDate = new Map<string, Task[]>();
                taskGroup.forEach(task => {
                    if (task.taskDate) {
                        const dateKey = task.taskDate.format('YYYY-MM-DD');
                        if (!tasksByDate.has(dateKey)) {
                            tasksByDate.set(dateKey, []);
                        }
                        tasksByDate.get(dateKey)!.push(task);
                    }
                });

                const stayDays: string[] = [];
                const current = stayFrom.clone().startOf('day');
                const end = stayTo.clone().startOf('day');
                while (current.isSameOrBefore(end, 'day')) {
                    stayDays.push(current.format('YYYY-MM-DD'));
                    current.add(1, 'day');
                }

                const stayFromDay = stayFrom.format('YYYY-MM-DD');
                const stayToDay = stayTo.format('YYYY-MM-DD');

                const filteredTasksByDate = new Map<string, Task[]>();
                tasksByDate.forEach((dayTasks, dateKey) => {
                    const filteredTasks = dayTasks.filter(task => {
                        if (!task.taskDate) return false;

                        if (dateKey === stayFromDay && task.taskDate.isBefore(stayFrom)) {
                            return false;
                        }

                        if (dateKey === stayToDay && task.taskDate.isSameOrAfter(stayTo)) {
                            return false;
                        }

                        return true;
                    });

                    if (filteredTasks.length > 0) {
                        filteredTasksByDate.set(dateKey, filteredTasks);
                    }
                });

                const dosesPerDay = Array.from(filteredTasksByDate.values()).map(dayTasks =>
                    Array.from(
                        new Set(dayTasks.map(task => task.taskDate?.format('h:mmA') || '').filter(time => time !== ''))
                    ).sort()
                );

                const allDaysHaveSameDoses =
                    dosesPerDay.length > 0 &&
                    dosesPerDay.every(
                        doses =>
                            doses.length === dosesPerDay[0].length &&
                            doses.every((dose, idx) => dose === dosesPerDay[0][idx])
                    );

                const hasTasksForAllDays = stayDays.every(day => filteredTasksByDate.has(day));

                const isDaily = hasTasksForAllDays && allDaysHaveSameDoses;

                if (isDaily) {
                    medications.push({
                        schedule: dosesPerDay[0] || [],
                        name: firstTask.medication?.medicationName || '',
                        methodType: firstTask.medication?.medicationMethodId,
                        dosage: {
                            quantity: firstTask.medication?.dosageAmount
                                ? parseFloat(firstTask.medication?.dosageAmount)
                                : undefined,
                            unit: firstTask.medication?.dosageUnitId
                        },
                        instructions: firstTask.medication?.medicationInstructions,
                        specificSchedule: []
                    });
                }
            }
        });

        return medications;
    };

    const getRebookDateAndDateEndForService = (
        originalStart: Moment,
        originalEnd: Moment,
        flowType?: 'grooming' | 'daycare' | 'boarding' | 'transportation' | 'walking' | 'training'
    ) => {
        const roundUpToNext15Minutes = (value: Moment): Moment => {
            const rounded = value.clone();
            const remainder = rounded.minutes() % 15;
            if (remainder !== 0) {
                rounded.add(15 - remainder, 'minutes');
            }
            return rounded;
        };

        const now = moment().tz(timezone);
        const start = originalStart.clone();
        const end = originalEnd.clone();
        const rebookStartDate = now.isBetween(start, end, undefined, '[)')
            ? end.clone()
            : now.isAfter(end)
            ? now.clone()
            : start.clone();
        const rebookStartDateRounded = roundUpToNext15Minutes(rebookStartDate);
        return {
            date: rebookStartDateRounded,
            dateEnd:
                flowType === 'boarding'
                    ? rebookStartDateRounded.clone().add(1, 'day')
                    : rebookStartDateRounded.clone().add(1, 'hour')
        };
    };

    const transformOrderToAppointments = (order: Order): Array<AppointmentMultiple> => {
        const petIds = getUniquePetIds(order);
        const appointments: Array<AppointmentMultiple> = [];

        const services: Array<PetServiceWrapper> = petServices.map(petService => ({
            ...petService,
            duration: {
                hours: Math.trunc(petService.durationInMinutes / 60),
                minutes: petService.durationInMinutes % 60
            }
        }));

        petIds.forEach(petId => {
            const appointment: AppointmentMultiple = {
                clientId: order.client.id!,
                petId: petId,
                createdByStaffId: auth.user!.staffId,
                notes: order.notes,
                variableData: []
            };

            order.items.appointments.forEach(item => {
                if (item.pet.id === petId && flow !== 'transportation') {
                    let daycareOrdBoardingToService: PetServiceWrapper | undefined = undefined;
                    const flow =
                        (item.appointment.services[0].businessArea.id as
                            | 'grooming'
                            | 'daycare'
                            | 'boarding'
                            | 'walking'
                            | 'training'
                            | undefined) ?? undefined;
                    const service = services.find(service => service.service.id === item.appointment.services[0].id);
                    const durationInMinutes =
                        flow === 'walking'
                            ? item.appointment?.details?.walking?.duration ?? 0
                            : flow === 'training'
                            ? item.appointment?.details?.training?.duration ?? 0
                            : item.appointment?.duration.to.diff(item.appointment?.duration.from, 'minutes');
                    const walkingOrTrainingDetails =
                        flow === 'walking' ? item.appointment?.details?.walking : item.appointment?.details?.training;

                    const rebookDates =
                        flow === 'daycare' || flow === 'boarding' || flow === 'walking' || flow === 'training'
                            ? getRebookDateAndDateEndForService(
                                  item.appointment.duration.from,
                                  item.appointment.duration.to,
                                  flow
                              )
                            : undefined;

                    if (flow === 'daycare' || flow === 'boarding') {
                        daycareOrdBoardingToService = {
                            service: {
                                id: item.appointment?.services[0]?.id || 0,
                                name: item.appointment?.services[0]?.name
                            },
                            price: Number(item.orderItem.amount),
                            durationInMinutes: durationInMinutes,
                            duration: {
                                hours: item.appointment?.duration ? Math.trunc(durationInMinutes / 60) : 0,
                                minutes: item.appointment?.duration ? durationInMinutes % 60 : 0
                            },
                            discount: {
                                type: item.orderItem?.discount?.percentage ? 'percentage' : 'amount',
                                value: item.orderItem?.discount?.percentage
                                    ? item.orderItem.discount.percentage
                                    : Number(item.orderItem.discount?.amount)
                            },
                            roomUnitId:
                                item.appointment.roomUnitId ||
                                item.appointment.tasks[0]?.feeding?.roomUnitId ||
                                item.appointment.tasks[0]?.medication?.roomUnitId,
                            feedings: transformToFeedings(item.appointment.tasks, true),
                            medications: transformToMedications(
                                item.appointment.tasks,
                                true,
                                flow,
                                item.appointment.duration.from,
                                item.appointment.duration.to
                            ),
                            businessArea: flow === 'daycare' ? 'daycare' : 'boarding',
                            color: item.appointment.services[0].color,
                            variableDuration: flow === 'daycare' ? service?.variableDuration : undefined
                        };
                    }

                    appointment.variableData.push({
                        service:
                            flow === 'daycare' || flow === 'boarding'
                                ? daycareOrdBoardingToService
                                : {
                                      service: item.appointment.services[0],
                                      price: Number(item.orderItem.amount),
                                      durationInMinutes:
                                          flow === 'walking' || flow === 'training'
                                              ? durationInMinutes
                                              : service?.durationInMinutes || 0,
                                      duration: {
                                          hours:
                                              flow === 'walking' || flow === 'training'
                                                  ? Math.trunc(durationInMinutes / 60)
                                                  : service?.durationInMinutes
                                                  ? Math.trunc(service?.durationInMinutes / 60)
                                                  : 0,
                                          minutes:
                                              flow === 'walking' || flow === 'training'
                                                  ? durationInMinutes % 60
                                                  : service?.durationInMinutes
                                                  ? service?.durationInMinutes % 60
                                                  : 0
                                      },
                                      businessArea:
                                          flow === 'walking'
                                              ? 'walking'
                                              : flow === 'training'
                                              ? 'training'
                                              : 'grooming',
                                      color: item.appointment.services[0].color,
                                      bookingType: item.appointment?.details?.training?.sessionType,
                                      inHomeService: walkingOrTrainingDetails?.type === 'in_home',
                                      address: walkingOrTrainingDetails?.address
                                  },
                        staffId: item.orderItem.staffId,
                        date:
                            flow === 'daycare' || flow === 'boarding' || flow === 'walking' || flow === 'training'
                                ? rebookDates!.date
                                : date,
                        dateEnd:
                            flow === 'daycare' || flow === 'boarding' || flow === 'walking' || flow === 'training'
                                ? rebookDates!.dateEnd
                                : undefined,
                        type: itemTypes.service,
                        itemData: {
                            discount: {
                                type: item.orderItem.discount?.percentage ? 'percentage' : 'amount',
                                value: item.orderItem.discount?.percentage
                                    ? item.orderItem.discount.percentage
                                    : Number(item.orderItem.discount?.amount)
                            },
                            quantity: undefined,
                            price: flow === 'daycare' || flow === 'boarding' ? 0 : Number(item.orderItem.amount),
                            total: flow === 'daycare' || flow === 'boarding' ? 0 : Number(item.orderItem.total),
                            flow: flow
                        }
                    });
                }
            });

            order.items.addOnServices.forEach(item => {
                if (item.orderItem.petId === petId) {
                    appointment.variableData.push({
                        addOn: item.addOnService,
                        staffId: item.orderItem.staffId,
                        date: date,
                        type: itemTypes.addOn,
                        itemData: {
                            discount: {
                                type: item.orderItem.discount?.percentage ? 'percentage' : 'amount',
                                value: item.orderItem.discount?.percentage
                                    ? item.orderItem.discount.percentage
                                    : Number(item.orderItem.discount?.amount)
                            },
                            quantity: item.orderItem.quantity,
                            price: Number(item.orderItem.amount),
                            total: Number(item.orderItem.amount) * item.orderItem.quantity
                        }
                    });
                }
            });

            order.items.products.forEach(item => {
                if (item.orderItem.petId === petId) {
                    appointment.variableData.push({
                        product: item.product,
                        staffId: item.orderItem.staffId,
                        date: date,
                        type: itemTypes.product,
                        itemData: {
                            discount: {
                                type: item.orderItem.discount?.percentage ? 'percentage' : 'amount',
                                value: item.orderItem.discount?.percentage
                                    ? item.orderItem.discount.percentage
                                    : Number(item.orderItem.discount?.amount)
                            },
                            quantity: item.orderItem.quantity,
                            price: Number(item.orderItem.amount),
                            total: Number(item.orderItem.amount) * item.orderItem.quantity
                        }
                    });
                }
            });

            order.items.customs.forEach(item => {
                if (item.orderItem.petId === petId) {
                    appointment.variableData.push({
                        customDescription: item.orderItem.description,
                        staffId: item.orderItem.staffId,
                        date: date,
                        type: itemTypes.custom,
                        itemData: {
                            discount: {
                                type: item.orderItem.discount?.percentage ? 'percentage' : 'amount',
                                value: item.orderItem.discount?.percentage
                                    ? item.orderItem.discount.percentage
                                    : Number(item.orderItem.discount?.amount)
                            },
                            quantity: item.orderItem.quantity,
                            price: Number(item.orderItem.amount),
                            total: Number(item.orderItem.total),
                            taxId: item.orderItem.taxId
                        }
                    });
                }
            });
            appointments.push(appointment);
        });

        return appointments;
    };

    const transformAppointmentOrderItemToAppointment = (
        appointmentItem: AppointmentOrderItem
    ): Array<AppointmentMultiple> => {
        const appointments: Array<AppointmentMultiple> = [];
        let daycareOrBoardingToService: PetServiceWrapper | undefined = undefined;
        const flow =
            (appointmentItem.appointment.services[0].businessArea.id as
                | 'grooming'
                | 'daycare'
                | 'boarding'
                | 'walking'
                | 'training'
                | undefined) ?? undefined;

        const services: Array<PetServiceWrapper> = petServices.map(petService => ({
            ...petService,
            duration: {
                hours: Math.trunc(petService.durationInMinutes / 60),
                minutes: petService.durationInMinutes % 60
            }
        }));

        const service = services.find(service => service.service.id === appointmentItem.appointment.services[0].id);

        const durationInMinutes =
            flow === 'walking'
                ? appointmentItem.appointment?.details?.walking?.duration ?? 0
                : flow === 'training'
                ? appointmentItem.appointment?.details?.training?.duration ?? 0
                : appointmentItem.appointment?.duration.to.diff(appointmentItem.appointment?.duration.from, 'minutes');
        const walkingOrTrainingDetails =
            flow === 'walking'
                ? appointmentItem.appointment?.details?.walking
                : appointmentItem.appointment?.details?.training;

        const rebookDates =
            flow === 'daycare' || flow === 'boarding' || flow === 'walking' || flow === 'training'
                ? getRebookDateAndDateEndForService(
                      appointmentItem.appointment.duration.from,
                      appointmentItem.appointment.duration.to,
                      flow
                  )
                : undefined;

        if (flow === 'daycare' || flow === 'boarding') {
            daycareOrBoardingToService = {
                service: {
                    id: appointmentItem.appointment?.services[0]?.id || 0,
                    name: appointmentItem.appointment?.services[0]?.name
                },
                price: Number(appointmentItem.orderItem.amount),
                durationInMinutes: durationInMinutes,
                duration: {
                    hours: appointmentItem.appointment?.duration ? Math.trunc(durationInMinutes / 60) : 0,
                    minutes: appointmentItem.appointment?.duration ? durationInMinutes % 60 : 0
                },
                discount: {
                    type: appointmentItem.orderItem?.discount?.percentage ? 'percentage' : 'amount',
                    value: appointmentItem.orderItem?.discount?.percentage
                        ? appointmentItem.orderItem.discount.percentage
                        : Number(appointmentItem.orderItem.discount?.amount)
                },
                roomUnitId:
                    appointmentItem.appointment.roomUnitId ||
                    appointmentItem.appointment.tasks[0]?.feeding?.roomUnitId ||
                    appointmentItem.appointment.tasks[0]?.medication?.roomUnitId,
                feedings: transformToFeedings(appointmentItem.appointment.tasks, true),
                medications: transformToMedications(
                    appointmentItem.appointment.tasks,
                    true,
                    flow,
                    appointmentItem.appointment.duration.from,
                    appointmentItem.appointment.duration.to
                ),
                businessArea: flow === 'daycare' ? 'daycare' : 'boarding',
                color: appointmentItem.appointment.services[0].color,
                variableDuration: flow === 'daycare' ? service?.variableDuration : undefined
            };
        }

        const appointment: AppointmentMultiple = {
            clientId: appointmentItem.appointment.customer.id!,
            petId: appointmentItem.orderItem.petId!,
            createdByStaffId: auth.user!.staffId,
            notes: appointmentItem.appointment.notes,
            variableData: [
                {
                    service:
                        flow === 'daycare' || flow === 'boarding'
                            ? daycareOrBoardingToService
                            : {
                                  service: appointmentItem.appointment.services[0],
                                  price: Number(appointmentItem.orderItem.amount),
                                  durationInMinutes:
                                      flow === 'walking' || flow === 'training'
                                          ? durationInMinutes
                                          : service?.durationInMinutes || 0,
                                  duration: {
                                      hours:
                                          flow === 'walking' || flow === 'training'
                                              ? Math.trunc(durationInMinutes / 60)
                                              : service?.durationInMinutes
                                              ? Math.trunc(service?.durationInMinutes / 60)
                                              : 0,
                                      minutes:
                                          flow === 'walking' || flow === 'training'
                                              ? durationInMinutes % 60
                                              : service?.durationInMinutes
                                              ? service?.durationInMinutes % 60
                                              : 0
                                  },
                                  businessArea:
                                      flow === 'walking' ? 'walking' : flow === 'training' ? 'training' : 'grooming',
                                  color: appointmentItem.appointment.services[0].color,
                                  bookingType: appointmentItem.appointment?.details?.training?.sessionType,
                                  inHomeService: walkingOrTrainingDetails?.type === 'in_home',
                                  address: walkingOrTrainingDetails?.address
                              },
                    staffId: appointmentItem.orderItem.staffId,
                    date:
                        flow === 'daycare' || flow === 'boarding' || flow === 'walking' || flow === 'training'
                            ? rebookDates!.date
                            : date,
                    dateEnd:
                        flow === 'daycare' || flow === 'boarding' || flow === 'walking' || flow === 'training'
                            ? rebookDates!.dateEnd
                            : undefined,
                    type: itemTypes.service,
                    itemData: {
                        discount: {
                            type: appointmentItem.orderItem.discount?.percentage ? 'percentage' : 'amount',
                            value: appointmentItem.orderItem.discount?.percentage
                                ? appointmentItem.orderItem.discount.percentage
                                : Number(appointmentItem.orderItem.discount?.amount)
                        },
                        quantity: undefined,
                        price: flow === 'daycare' || flow === 'boarding' ? 0 : Number(appointmentItem.orderItem.amount),
                        total: flow === 'daycare' || flow === 'boarding' ? 0 : Number(appointmentItem.orderItem.total),
                        flow: flow
                    }
                }
            ]
        };
        appointments.push(appointment);

        return appointments;
    };

    useEffect(() => {
        if (props.at) {
            setDate(props.at);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.at]);

    // Fetch evaluation service when isEvaluation is true
    useEffect(() => {
        if (isEvaluationActive && !evaluationService && serviceStatus !== ServicesStatus.Fetching) {
            dispatch(fetchEvaluationServiceThunk());
        }
    }, [isEvaluationActive, evaluationService, serviceStatus]);

    // Convert evaluation service to PetServiceWrapper and pre-select it
    useEffect(() => {
        if (isEvaluationActive && evaluationService && evaluationService.id && !service) {
            const evaluationPetService: PetServiceWrapper = {
                service: evaluationService as any, // Type assertion to handle Service type mismatch
                durationInMinutes:
                    isServiceDuration(evaluationService.duration) && evaluationService.duration.simple
                        ? evaluationService.duration.simple.hours * 60 + evaluationService.duration.simple.minutes
                        : 60,
                price: evaluationService.pricing.fixedPrice?.[0]?.price || 0,
                businessArea: '',
                color: evaluationService.color ?? ''
            };
            setService(evaluationPetService);
        }
    }, [isEvaluationActive, evaluationService, service]);

    useEffect(() => {
        if (
            !showComplementaryItem &&
            !showPetAndServices &&
            !appointmentCreated &&
            !orderData.showDetail &&
            !orderData.showCheckout &&
            !depositCollect &&
            parentId && // Add guard to ensure parentId exists
            date // Add guard to ensure date exists
        ) {
            dispatch(
                fetchAlertsThunk(
                    apiClientWrapper,
                    parentId || 0,
                    petId || 0,
                    date,
                    isRecurring,
                    service?.service?.id,
                    staffId,
                    props.appointmentId
                )
            );
        }
    }, [
        date,
        parentId,
        petId,
        service?.service?.id,
        staffId,
        isRecurring,
        showComplementaryItem,
        showPetAndServices,
        appointmentCreated,
        orderData.showDetail,
        orderData.showCheckout,
        depositCollect,
        props.appointmentId
    ]);

    const getQuantityItems = () => {
        let quantity = 0;
        multipleAppointments.forEach(app => {
            app.variableData?.forEach(data => {
                quantity++;
            });
        });
        return quantity;
    };

    useEffect(() => {
        props.onChangeTime &&
            props.onChangeTime(
                date,
                multipleAppointments.length > 0 ? serviceTotal?.durationInMinutes : service?.durationInMinutes,
                staffId
            );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, service, staffId, multipleAppointments, serviceTotal]);

    useEffect(() => {
        setServicesUsed(getServicesUsed());
        setItemsUsed(getItemsUsed());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [petId, type]);

    useEffect(() => {
        setExceededDeposit(
            marketplace.deposit.type === MarketplaceDepositType.Amount &&
                convertToNumber(marketplace.deposit.value || '0') * getQuantityItems() >
                    (serviceTotal?.price || service?.price || 0)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceTotal]);

    useEffect(() => {
        if (haveMultiplePets() || multipleAppointments.length > 0) {
            setShowDepositAndNote(true);
            if (
                multipleAppointments.some((appt: AppointmentMultiple) =>
                    appt.variableData.some(
                        (data: DataVaraible) =>
                            data.itemData?.flow === 'daycare' ||
                            data.itemData?.flow === 'boarding' ||
                            data.itemData?.flow === 'transportation' ||
                            data.itemData?.flow === 'walking' ||
                            data.itemData?.flow === 'training'
                    )
                )
            ) {
                setIsDaycareBoardingTransportationTrainingOrWalking(true);
            }
        }
        if (multipleAppointments.length > 0) {
            let priceTotal = 0;
            let durationMinutesTotal = 0;
            let durationTotal: Duration = {
                hours: undefined,
                minutes: undefined
            };
            let minorDate = date;
            multipleAppointments.forEach((apt: AppointmentMultiple) => {
                apt.variableData?.forEach((apt: DataVaraible) => {
                    if (apt.date.isBefore(minorDate)) {
                        minorDate = apt.date;
                    }
                    if (apt.itemData?.flow === 'boarding') {
                        const lineTotal = getBoardingStayLineTotal({
                            date: apt.date,
                            dateEnd: apt.dateEnd,
                            servicePrice: apt.service?.price,
                            itemDataPrice: apt.itemData?.price,
                            selectedOffer: apt.service?.selectedOffer ?? null
                        });
                        priceTotal = priceTotal + lineTotal;
                    } else if (apt.itemData?.flow === 'daycare') {
                        let variableDuration: { duration_option?: string; durationOption?: string } | undefined;
                        if (apt.service?.variableDuration) {
                            try {
                                variableDuration = JSON.parse(apt.service.variableDuration);
                            } catch {
                                variableDuration = undefined;
                            }
                        }

                        const daycareDurationOption =
                            variableDuration?.duration_option ?? variableDuration?.durationOption;
                        if (daycareDurationOption === 'hourly') {
                            const durationMinutes =
                                apt.dateEnd != null
                                    ? Math.max(0, moment(apt.dateEnd).diff(moment(apt.date), 'minutes'))
                                    : apt.service?.durationInMinutes || 0;
                            const hours = Math.max(0, Math.ceil(durationMinutes / 60));
                            const unitPrice = Number(apt.service?.price) || Number(apt.itemData?.price) || 0;
                            const hourlySubtotal = unitPrice * hours;
                            if (apt.service?.enrollment?.id != null) {
                                priceTotal = priceTotal + hourlySubtotal;
                            } else {
                                const hourlyWithDiscount = getPriceWithDiscount(apt, hourlySubtotal);
                                priceTotal =
                                    priceTotal +
                                    (hourlyWithDiscount !== undefined ? hourlyWithDiscount : hourlySubtotal);
                            }
                        } else if (apt.service?.enrollment?.id != null) {
                            // Membership price is already applied on service.price
                            priceTotal = priceTotal + (Number(apt.service?.price) || 0);
                        } else {
                            priceTotal =
                                priceTotal +
                                (getPriceWithDiscount(apt, apt.service?.price) ||
                                    getPriceWithDiscount(apt, apt.itemData?.total) ||
                                    0);
                        }
                    } else if (apt.service?.enrollment?.id != null) {
                        // Membership price is already applied on service.price (e.g. walking)
                        priceTotal = priceTotal + (Number(apt.service?.price) || 0);
                    } else {
                        priceTotal =
                            priceTotal +
                            (getPriceWithDiscount(apt, apt.service?.price) ||
                                getPriceWithDiscount(apt, apt.itemData?.total) ||
                                0);
                    }
                    if (apt.itemData?.flow === 'boarding') {
                        durationMinutesTotal =
                            durationMinutesTotal + getBoardingStayDurationMinutes(apt.date, apt.dateEnd);
                    } else {
                        durationMinutesTotal = durationMinutesTotal + (apt.service?.durationInMinutes || 0);
                    }
                });
            });
            durationTotal = {
                hours: Math.trunc(durationMinutesTotal / 60),
                minutes: durationMinutesTotal % 60
            };
            setServiceTotal({
                price: priceTotal,
                durationInMinutes: durationMinutesTotal,
                duration: durationTotal
            });
            setShowDepositAndNote(true);
            setDate(minorDate);
        } else {
            setServiceTotal(undefined);
            setShowDepositAndNote(false);
            setIsDaycareBoardingTransportationTrainingOrWalking(false);
        }
        setPetsUsed(getPetsUsed());
        if (rebook && multipleAppointments.length > 0) {
            setServicesUsed(getServicesUsed());
            setRebook(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [multipleAppointments]);

    useEffect(() => {
        if (props.order) {
            setRebook(true);
            setLoading(true);
            const appointmentToRebook = props.order.items.appointments[0];
            const appointmentFlow =
                (appointmentToRebook.appointment.services[0].businessArea.id as
                    | 'grooming'
                    | 'daycare'
                    | 'boarding'
                    | 'transportation'
                    | 'walking'
                    | 'training'
                    | undefined) ?? undefined;
            const { date: rebookDate, dateEnd: rebookDateEnd } = getRebookDateAndDateEndForService(
                appointmentToRebook.appointment.duration.from,
                appointmentToRebook.appointment.duration.to,
                appointmentFlow
            );
            setDate(rebookDate);
            setDateToEdit(rebookDate.clone());
            setDateEnd(rebookDateEnd);
            setParentId(appointmentToRebook.appointment.customer.id);
            setPetId(appointmentToRebook.appointment.pet.id);
            setVersionPetAndService(undefined);
            dispatch(fetchPetServicesThunk(apiClientWrapper, appointmentToRebook.appointment.pet.id));
        }
        if (props.appointmentItem) {
            setRebook(true);
            setLoading(true);
            const appointmentFlow =
                (props.appointmentItem.appointment.services[0].businessArea.id as
                    | 'grooming'
                    | 'daycare'
                    | 'boarding'
                    | 'transportation'
                    | 'walking'
                    | 'training'
                    | undefined) ?? undefined;
            const { date: rebookDate, dateEnd: rebookDateEnd } = getRebookDateAndDateEndForService(
                props.appointmentItem.appointment.duration.from,
                props.appointmentItem.appointment.duration.to,
                appointmentFlow
            );
            setDate(rebookDate);
            setDateToEdit(rebookDate.clone());
            setDateEnd(rebookDateEnd);
            setVersionPetAndService(undefined);
            setParentId(props.appointmentItem.appointment.customer.id);
            setPetId(props.appointmentItem.appointment.pet.id);
            if (flow === 'daycare' || flow === 'boarding' || flow === 'walking' || flow === 'training') {
                setMultipleAppointments(transformAppointmentOrderItemToAppointment(props.appointmentItem));
            } else {
                dispatch(fetchPetServicesThunk(apiClientWrapper, props.appointmentItem.appointment.pet.id));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.order, props.appointmentItem]);

    useNonInitialEffect(() => {
        switch (newBookingsStatus) {
            case NewBookingsStatus.SavingMultiple:
            case NewBookingsStatus.Saving:
                props.onStartBooking && props.onStartBooking();
                break;
            case NewBookingsStatus.SaveMultipleSuccess:
            case NewBookingsStatus.SaveSuccess:
                setSaving(false);
                setDepositCollect(false);
                setDepositSkipped(false);
                props.onChangeNewBooking && props.onChangeNewBooking(false);
                if (!bookingDone) {
                    dispatch(fetchOnboardingStepsThunk());
                }
                if (!closedForMultiDaycareRef.current) {
                    setAppointmentCreated(true);
                }
                break;
            case NewBookingsStatus.SaveForDepositSuccess:
            case NewBookingsStatus.SaveMultipleBookingForDepositSuccess:
                setDepositSkipped(false);
                setDepositCollect(true);
                setSaving(false);
                setOrderData(prev => ({
                    ...prev,
                    orderId: savedBooking?.appointments[0]?.orderId,
                    bookingId: savedBooking?.id,
                    showDetail: false
                }));
                break;
            case NewBookingsStatus.FetchPetServicesSuccess:
                setLoading(false);
                if (props.order && rebook) {
                    setMultipleAppointments(transformOrderToAppointments(props.order));
                }
                if (props.appointmentItem && rebook) {
                    setMultipleAppointments(transformAppointmentOrderItemToAppointment(props.appointmentItem));
                }
                break;
            case NewBookingsStatus.FetchAlertsSuccess:
                const alertBusinessHour = alerts?.filter(alert => alert.alertType === 'date_time');
                if (alertBusinessHour && alertBusinessHour?.length > 0) {
                    setShowAlertBusinessHour(true);
                } else {
                    setShowAlertBusinessHour(false);
                }
                setLoading(false);
                break;
            case NewBookingsStatus.Error:
                setSaving(false);
                setLoading(false);
                break;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newBookingsStatus]);

    useNonInitialEffect(() => {
        switch (bookingStatus) {
            case BookingsStatus.DeleteSuccess:
                setDepositCollect(false);
                setSaving(false);
                break;
            case BookingsStatus.Error:
                setSaving(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingStatus]);

    useEffect(() => {
        switch (serviceStatus) {
            case ServicesStatus.Fetching:
                setLoading(true);
                break;
            case ServicesStatus.FetchSuccess:
                setLoading(false);
                break;
            case ServicesStatus.Error:
                setLoading(false);
                break;
        }
    }, [serviceStatus]);

    useNonInitialEffect(() => {
        switch (appointmentsStatus) {
            case AppointmentsStatus.Error:
                setSaving(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appointmentsStatus]);

    useNonInitialEffect(() => {
        setTouched(true);
        props.onChangeNewBooking && props.onChangeNewBooking(true);
    }, [staffId, parentId, date, isRecurring]);

    useNonInitialEffect(() => {
        if (loadingAppointments) {
            setTimeout(() => setLoadingAppointments(false), 1000);
        }
    }, [loadingAppointments]);

    useEffect(() => {
        if (props.parentID && (!props.fromClient || !props.petID)) {
            dispatch(fetchPetsThunk(apiClientWrapper, props.parentID!));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.parentID, props.fromClient, props.petID]);

    const bookingDisabled =
        multipleAppointments.length === 0 ||
        (showLocationSelector && !selectedMarketplaceId) ||
        (showPetAndServices && (!petId || !service || !staffId)) ||
        withoutService();

    const recurringTemplate: RecurringSeriesTemplate | undefined = useMemo(
        () =>
            showRecurringPanel && !bookingDisabled
                ? { appointments: getMultipleBookingToSave(), order: getComplementaryItemsToSave() }
                : undefined,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [showRecurringPanel, bookingDisabled, multipleAppointments, notes]
    );

    const canCreateRecurring =
        !recurringSeriesCreated &&
        !isEvaluationActive &&
        !isCollectDeposit &&
        !depositCollect &&
        !appointmentCreated &&
        !showPetAndServices &&
        !showComplementaryItem &&
        !orderData.showDetail &&
        !orderData.showCheckout;

    const recurringPanel = recurringConfig && showRecurringPanel && (
        <RecurringSeriesPanel
            config={recurringConfig}
            template={recurringTemplate}
            multipleAppointments={multipleAppointments}
            bookingDisabled={bookingDisabled}
            inline={isStackedRecurring}
            onChange={setRecurringConfig}
            onClose={handleCloseRecurring}
            onSeriesCreated={handleSeriesCreated}
        />
    );

    const createRecurringLink = canCreateRecurring && (
        <Box
            id="booking_link_create_recurring"
            aria-disabled={showRecurringPanel}
            className={clsx(classes.createRecurringLink, {
                [classes.createRecurringLinkDisabled]: showRecurringPanel
            })}
            onClick={showRecurringPanel ? undefined : handleOpenRecurring}
        >
            <FontAwesomeIcon
                icon={faRotate}
                className={classes.createRecurringIcon}
            />
            <Typography className={classes.createRecurringLabel}>Create Recurring</Typography>
        </Box>
    );

    const recurringUuid =
        props.appointmentItem?.appointment.recurringUuid ??
        props.order?.items.appointments[0]?.appointment.recurringUuid;

    const editRecurringLink = recurringUuid && isLargeDesktop && (
        <Box
            id="booking_link_edit_recurring"
            className={classes.createRecurringLink}
            onClick={() => setShowRecurringSeriesPanel(true)}
        >
            <FontAwesomeIcon
                icon={faRotate}
                className={classes.createRecurringIcon}
            />
            <Typography className={classes.createRecurringLabel}>Edit Recurring</Typography>
        </Box>
    );

    const recurringSeriesPanel = recurringUuid && showRecurringSeriesPanel && (
        <RecurringSeriesViewPanel
            recurringUuid={recurringUuid}
            onClose={() => setShowRecurringSeriesPanel(false)}
        />
    );

    const locationView = showLocationSelector && (
        <Box className={clsx(classes.sectionContainer, classes.locationSection)}>
            <LocationSelector
                value={selectedMarketplaceId}
                options={bookingLocations}
                disabled={locationReadOnly}
                onChange={changeLocationHandler}
            />
        </Box>
    );

    const clientView = (
        <Box className={classes.sectionContainer}>
            <ClientSearch
                key={selectedMarketplaceId}
                fromClient={props.fromClient}
                parentID={parentId as number}
                parentName={parentName as string}
                setPetParent={handlerParentID}
                onAddClient={addNewClient}
                setPetId={value => setPetId(value)}
                marketplaceId={selectedMarketplaceId}
                readOnly={!!(props.parentID && !props.clientAdded)}
            />
        </Box>
    );

    const petsView = (
        <Box className={clsx(classes.sectionContainer, classes.marginTop)}>
            <PetSearch
                hideSelect={true}
                parentID={parentId}
                petId={petId}
                readOnly={!!(props.petID && !props.petAdded)}
                handlerPet={noop}
                onAddPet={props.onAddPet}
                multiplePets={true}
            />
        </Box>
    );

    const dateView = (
        <DateSelector
            isCollectDeposit={isCollectDeposit}
            date={date}
            withWarning={!(props.order || props.appointmentItem)}
            showAlertBusinessHour={showAlertBusinessHour}
            multiplePet={!!(multipleAppointments.length > 1 || multipleAppointments[0]?.variableData?.length > 1)}
            recurringAction={editRecurringLink || createRecurringLink}
            onChangeDate={handlerChangeGeneralDate}
            readOnly={false}
            disabled={isDaycareBoardingTransportationTrainingOrWalking}
            alerts={alerts?.filter(alert => alert.alertType === 'date_time' || alert.alertType === 'unified')}
        />
    );

    const depositView = (
        <DepositSelector
            active={isCollectDeposit}
            servicePrice={service?.price}
            alerts={alerts?.filter(alert => alert.alertType === 'deposit')}
            readOnly={false}
            isRecurrent={isRecurring}
            onChangeDeposit={handleChangeDeposit}
        />
    );

    const notesView = (
        <Box className={classes.sectionContainer}>
            <Notes
                title="Booking Notes"
                placeholder="Notes here..."
                staffId={staffId}
                notes={notes}
                onChangeNote={(value: string) => setNotes(value)}
            />
        </Box>
    );

    const appointmentBookedView = (
        <Box className={classes.appointmentCreatedContainer}>
            <AppointmentCreated
                withDeposit={isCollectDeposit && !exceededDeposit && !depositSkipped}
                multipleAppointments={multipleAppointments}
                date={date}
                petId={petId}
                depositPaidAmount={depositPaidAmount}
                onGoToBooking={() => {
                    props.onRefreshBooking && props.onRefreshBooking();
                    setOrderData(prev => ({ ...prev, showDetail: true }));
                }}
                onBooked={props.onRefreshBooking && props.onRefreshBooking}
            />
        </Box>
    );

    const multiplePetsView = (
        <Box>
            {loadingAppointments ? (
                <Spinner />
            ) : (
                <MultiplePets
                    date={date}
                    multipleAppointments={paginatedMultipleAppointments}
                    allMultipleAppointments={multipleAppointments}
                    isEvaluation={isEvaluationActive}
                    onAddAnotherPet={handlerAddAnotherPet}
                    onAddService={(id: number) => handlerAddItem(id, itemTypes.service)}
                    onAddAddOn={(id: number) => {
                        handlerAddItem(id, itemTypes.addOn);
                    }}
                    onAddCustomItem={(id: number) => handlerAddItem(id, itemTypes.custom)}
                    onAddProduct={(id: number) => handlerAddItem(id, itemTypes.product)}
                    onDelete={handlerDeleteAppointmentsPet}
                    onEdit={handlerEditAppointmentPet}
                    onDeleteAppointment={handlerDeleteAppointmentPet}
                    onShowActions={(
                        petIdShow: number,
                        dateShow: Moment,
                        itemDataShow?: ItemData,
                        serviceShow?: PetServiceWrapper,
                        staffIdShow?: number,
                        productShow?: Product,
                        addOnShow?: AddOnService,
                        customShow?: string,
                        typeShow?: itemTypes,
                        dateEndShow?: Moment
                    ) =>
                        setShowActions({
                            petId: petIdShow,
                            service: serviceShow,
                            staffId: staffIdShow,
                            itemData: itemDataShow,
                            date: dateShow,
                            product: productShow,
                            addOn: addOnShow,
                            custom: customShow,
                            type: typeShow,
                            dateEnd: dateEndShow
                        })
                    }
                    onShowPetCard={(petIdSelected: number) =>
                        props.onShowPetCard && props.onShowPetCard(petIdSelected, parentId!)
                    }
                    onAddEvaluation={handleAddEvaluation}
                    onViewResults={props.onViewResults}
                />
            )}
            {!loadingAppointments && multipleAppointments.length > 0 && (
                <AppointmentsPaginator
                    page={appointmentsPage}
                    totalPages={appointmentsTotalPages}
                    onPageChange={setAppointmentsPage}
                />
            )}
        </Box>
    );

    const petAndServiceView = (
        <PetAndServices
            parentId={parentId!}
            petId={petId}
            service={service}
            staffId={staffId}
            staffIdPreselected={props.staffId}
            discount={discount}
            servicesUsed={servicesUsed}
            petsUsed={petsUsed}
            flow={props.isEvaluation || isEvaluationActive ? 'grooming' : flow}
            versionPetAndServices={versionPetAndService}
            date={dateToEdit ?? date}
            dateEnd={dateEnd}
            roomUnitId={service?.roomUnitId}
            isEvaluation={isEvaluationActive}
            petWithoutSelect={
                versionPetAndService !== versionPetAndServices.add &&
                versionPetAndService !== versionPetAndServices.another
            }
            transportationType={transportationType}
            onBack={backPetAndServicesOrComplementaryItem}
            onBook={handlerSavePetAndServices}
            onRemove={handlerRemoveAppointments}
            onSaveDaycare={handlerSaveDaycare}
            onSaveBoarding={handlerSaveBoarding}
            onSaveTransportation={handlerSaveTransportation}
            onSaveWalking={handlerSaveWalking}
            draftSessionBookings={draftSessionBookings}
            onSaveTraining={handlerSaveTraining}
            initialMembershipSelection={membershipPrefillEnabled ? props.initialMembershipSelection : undefined}
            initialMembershipServiceId={membershipPrefillEnabled ? props.initialMembershipServiceId : undefined}
            disableMembershipsAutoSelect={membershipPrefillEnabled}
        />
    );

    const primaryServiceStaffId = useMemo(() => {
        const appt = multipleAppointments.find(a => a.petId === petId);
        const mainService = appt?.variableData?.find((d: DataVaraible) => d.service != null);
        return mainService?.staffId;
    }, [multipleAppointments, petId]);

    const petBusinessAreas = useMemo(() => {
        const appt = multipleAppointments.find(a => a.petId === petId);
        const areas = (appt?.variableData ?? [])
            .filter((d: DataVaraible) => d.service != null)
            .map((d: DataVaraible) => d.service!.businessArea)
            .filter(Boolean);
        return Array.from(new Set(areas));
    }, [multipleAppointments, petId]);

    const complementaryItemView = (
        <ComplementaryItem
            parentId={parentId!}
            petId={petId}
            type={type}
            versionPetAndServices={versionPetAndService}
            date={dateToEdit ?? date}
            product={product}
            addOn={addOn}
            custom={custom}
            staffId={staffId}
            quantity={quantity}
            amount={amount}
            taxId={taxId}
            discount={discount}
            itemsUsed={itemsUsed}
            petWithoutSelect={true}
            dates={datesAddOn}
            primaryServiceStaffId={primaryServiceStaffId}
            petBusinessAreas={petBusinessAreas}
            onBack={backPetAndServicesOrComplementaryItem}
            onBook={handlerSaveItems}
            onRemove={handlerRemoveItems}
        />
    );

    const orderDetailView = (
        <OrderDetail
            orderId={orderData.orderId!}
            bookingId={orderData.bookingId!}
            onClose={handleCloseBooking}
            onCheckout={(bookingId, orderId) =>
                setOrderData(prev => ({ ...prev, bookingId, orderId, showDetail: false, showCheckout: true }))
            }
        />
    );

    const orderCheckoutView = (
        <Checkout
            orderId={orderData.orderId!}
            bookingId={orderData.bookingId!}
            onClose={handleCloseBooking}
            onBack={() => setOrderData(prev => ({ ...prev, showDetail: true, showCheckout: false }))}
            onViewBooking={() => setOrderData(prev => ({ ...prev, showDetail: true, showCheckout: false }))}
        />
    );

    const confirmCloseDialog = (
        <ConfirmClosePopup
            title="Exit Booking"
            question="Your changes will not be saved. Are you sure you want to exit this booking?"
            onDiscard={() =>
                setClosing(prev => ({
                    ...prev,
                    close: false,
                    tabChange: false
                }))
            }
            onSave={() => handleClose(true)}
        />
    );

    const daycareFastCheckIn = <DaycareFastCheckIn date={date} />;

    return (
        <BookingLocationContext.Provider value={{ apiClientWrapper, timeZone: timezone }}>
            <Box
                className={clsx(classes.container, {
                    [classes.withoutEvents]: saving,
                    [classes.containerStacked]: isStackedRecurring && !!recurringPanel
                })}
            >
                {loading ? (
                    <Box className={classes.loadingContainer}>
                        <CircularProgress
                            size={isMobile ? '1.5rem' : '2.5rem'}
                            style={{ color: '#EAB464' }}
                        />
                    </Box>
                ) : (
                    <>
                        {!appointmentCreated && !showPetAndServices && !showComplementaryItem && (
                            <Box className={classes.headerContainer}>
                                <CreateBookingHeader
                                    title={
                                        props.isDaycareFastCheckIn
                                            ? 'Fast Check In'
                                            : depositCollect
                                            ? 'Collect Deposit'
                                            : appointmentCreated
                                            ? ''
                                            : isEvaluationActive
                                            ? 'Add Evaluation'
                                            : 'Add Appointment'
                                    }
                                    depositCollect={depositCollect}
                                    onClose={
                                        isLargeDesktop
                                            ? undefined
                                            : showPetAndServices || showComplementaryItem
                                            ? undefined
                                            : appointmentCreated
                                            ? handleBookedBooking
                                            : !touched && isMobile
                                            ? handleCloseWithOutModal
                                            : handleClosing
                                    }
                                    onBack={
                                        depositCollect
                                            ? backHandler
                                            : showPetAndServices || showComplementaryItem
                                            ? backPetAndServicesOrComplementaryItem
                                            : undefined
                                    }
                                    onShowBlockedTime={
                                        props.onShowBlockedTime && !appointmentCreated ? changeTabHandler : undefined
                                    }
                                />
                            </Box>
                        )}
                        {orderData.showDetail ? (
                            orderDetailView
                        ) : orderData.showCheckout ? (
                            orderCheckoutView
                        ) : appointmentCreated ? (
                            appointmentBookedView
                        ) : showPetAndServices ? (
                            petAndServiceView
                        ) : showComplementaryItem ? (
                            complementaryItemView
                        ) : props.isDaycareFastCheckIn ? (
                            daycareFastCheckIn
                        ) : (
                            <OverlayScrollbarsComponent style={{ height: '100%' }}>
                                <Box
                                    className={clsx(classes.appointmentContainer, {
                                        [classes.withoutEvents]: !!recurringSeriesCreated
                                    })}
                                >
                                    {depositCollect ? (
                                        <CollectDeposit
                                            bookingId={savedBooking!.id!}
                                            onClose={handleCloseBooking}
                                            onStartBooking={props.onStartBooking}
                                            onCollected={handlerCollected}
                                            allowSkipAndBook={props.fromClient !== true}
                                            onSkipAndBook={handlerSkipAndBook}
                                        />
                                    ) : (
                                        <Fragment>
                                            {locationView}
                                            {dateView}
                                            {clientView}
                                            {parentId &&
                                                petsState.list.filter(pet => !pet.deleted && !pet.deceased).length ===
                                                    0 &&
                                                petsView}
                                            {parentId &&
                                                petsState.list.filter(pet => !pet.deleted && !pet.deceased).length >
                                                    0 &&
                                                multiplePetsView}
                                            {showDepositAndNote && notesView}
                                            {showDepositAndNote &&
                                                !exceededDeposit &&
                                                marketplace.deposit.type !== MarketplaceDepositType.None &&
                                                takeDepositPayments &&
                                                depositView}
                                        </Fragment>
                                    )}
                                </Box>
                            </OverlayScrollbarsComponent>
                        )}
                        {(!props.isDaycareFastCheckIn || (props.isDaycareFastCheckIn && appointmentCreated)) &&
                            !depositCollect &&
                            !showPetAndServices &&
                            !showComplementaryItem &&
                            !(isCollectDeposit && !exceededDeposit && appointmentCreated && !depositSkipped) && (
                                <Box
                                    className={clsx(classes.totalContainer, {
                                        [classes.marginTotalContainer]: !appointmentCreated
                                    })}
                                >
                                    <FooterComponent
                                        success={appointmentCreated}
                                        withTotal={!appointmentCreated}
                                        title={'Total'}
                                        loading={saving}
                                        fastCheckIn={props.isDaycareFastCheckIn}
                                        total={
                                            appointmentCreated
                                                ? undefined
                                                : multipleAppointments.length > 0
                                                ? serviceTotal?.price
                                                : isEvaluationActive && staffId
                                                ? service?.price
                                                : 0
                                        }
                                        hours={serviceTotal?.duration?.hours}
                                        minutes={serviceTotal?.duration?.minutes}
                                        nights={
                                            isBoarding
                                                ? (() => {
                                                      const boardingStay = multipleAppointments
                                                          .flatMap(appt => appt.variableData ?? [])
                                                          .find(v => v.itemData?.flow === 'boarding');
                                                      const nights = boardingStay
                                                          ? getBoardingStayNightsCount(
                                                                boardingStay.date,
                                                                boardingStay.dateEnd
                                                            )
                                                          : 1;
                                                      return `(${nights} Night${nights !== 1 ? 's' : ''})`;
                                                  })()
                                                : undefined
                                        }
                                        buttonText={
                                            isCollectDeposit && !appointmentCreated && !exceededDeposit
                                                ? 'Pay & Book Appoinment'
                                                : appointmentCreated
                                                ? 'Done'
                                                : 'Book Appoinment'
                                        }
                                        disabledButton={
                                            bookingDisabled ||
                                            showRecurringPanel ||
                                            !!recurringSeriesCreated ||
                                            (!!recurringConfig &&
                                                !isRecurringSeriesConfigValid(
                                                    recurringConfig,
                                                    getSeriesAnchorDate(multipleAppointments)
                                                ))
                                        }
                                        deposit={undefined}
                                        onBook={handleBooked}
                                    />
                                </Box>
                            )}
                        {!isStackedRecurring && recurringPanel}
                        {recurringSeriesPanel}
                        {(closing.close || closing.tabChange) && confirmCloseDialog}
                        {showActions?.petId && (
                            <ActionsPopup
                                actions={[
                                    {
                                        label: getNameItem(showActions.type!),
                                        icon: faPencil,
                                        onClick: () =>
                                            handlerEditAppointmentPet(
                                                showActions.petId!,
                                                showActions.date!,
                                                showActions.staffId,
                                                showActions.itemData,
                                                showActions.service,
                                                showActions.product,
                                                showActions.addOn,
                                                showActions.custom,
                                                showActions.type,
                                                showActions.dateEnd
                                            )
                                    },
                                    {
                                        label: 'Delete',
                                        icon: faTrashAlt,
                                        color: '#EF4F57',
                                        onClick: () =>
                                            handlerDeleteAppointmentPet(
                                                showActions.petId!,
                                                getItemToDelete(showActions.type!),
                                                showActions.type,
                                                showActions.date
                                            )
                                    }
                                ]}
                                onClose={() =>
                                    setShowActions({
                                        date: undefined,
                                        staffId: undefined,
                                        service: undefined,
                                        petId: undefined,
                                        product: undefined,
                                        addOn: undefined,
                                        custom: undefined,
                                        type: undefined,
                                        discount: undefined,
                                        itemData: undefined,
                                        dateEnd: undefined
                                    })
                                }
                            />
                        )}
                    </>
                )}
            </Box>
            {isStackedRecurring && recurringPanel}
        </BookingLocationContext.Provider>
    );
};

export default CreateBooking;
